import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Play, Pause, RotateCcw, RotateCw, Volume2, VolumeX, Maximize2, Minimize2, SkipBack, SkipForward } from "lucide-react";
import { getResumePosition, saveResumePosition, clearResumePosition } from "@/lib/watchProgress";
import { loadYouTubeApi } from "@/lib/ytApi";
import { ensureBroadcast, recordDuration } from "@/lib/ytBroadcast";
import { useLive } from "@/lib/useLive";
import { interpolateWelcome } from "@/lib/welcomeTokens";
import LiveChat from "./LiveChat";
import PledgeDriveIdent from "./PledgeDriveIdent";
import ThanksBumper from "./ThanksBumper";
import PatronBug from "./PatronBug";
import IdleReel from "./IdleReel";
import { patchPrefs } from "@/lib/viewerPrefs";
import { isBrowserCacheableHost } from "@/lib/externalImage";

function formatTime(s) {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${ss}`;
}

// Strip a trailing video/audio file extension from a tape title so the
// NOW BROADCASTING / COMING UP slates show "Star Wars 1977" instead of
// "Star Wars 1977.mp4". Only fires when the extension is one we
// actually accept as media — avoids accidentally chopping a legitimate
// title that happens to end in ".tv" or ".mov" (e.g. "Star Wars 1977.tv").
const _MEDIA_EXTS = new Set([
  "mp4", "m4v", "mov", "webm", "mkv", "avi", "wmv", "flv", "mpg", "mpeg",
  "ogv", "ogg", "ts", "m4a", "mp3", "wav", "aac", "flac",
]);
function stripMediaExt(title) {
  if (!title || typeof title !== "string") return title || "";
  const dot = title.lastIndexOf(".");
  if (dot <= 0 || dot >= title.length - 1) return title;
  const ext = title.slice(dot + 1).toLowerCase();
  if (!_MEDIA_EXTS.has(ext)) return title;
  return title.slice(0, dot);
}

export default function CRT({
  power,
  isStatic,
  mode,
  currentChannel,
  currentVideo,
  channelNumber,
  channelNumberLabel,
  channelName,
  channelIsSubChannel,
  channelParentName,
  channelDescription,
  videoCount,
  videoIndex,
  playlist,
  initialSeekSec,
  liveTuneActive = false,
  selectTick,
  programmeHandoffTick = 0,
  peekTick,
  epgPeek,
  scanlineLevel,
  scanlineLabel,
  viewerTips,
  welcomeMessage,
  volume,
  onVolumeChange,
  onStepVideo,
  osdChannel,
  osdVolume,
  digitBuffer = "",
  onVideoEnded,
  apiBase,
  isOwner,
  maintenanceSuspended = false,
  maintenanceWakeOnly = false,
  fireTrophy,
  brandText,
  callsign,
  reduceMotion,
  silentAutoSkip,
  powerOnAnimation,
  tubeGlow,
  tubeGlowIntensity,
  tubeGlowColor,
  tubeGlowAnimated,
  tubeGlowAudioReactive,
  pictureMode = "letterbox",
  authUser,
  whatsNew,
  channels,
  videosByChannel,
  lineupReady = true,
  overlay,
  // Station-wide target loudness in LUFS (fetched from
  // /api/site/audio-settings). null while the value is loading; falls
  // back to -16 LUFS inside the CRT so we always have something usable.
  audioTargetLufs,
  // BMC pledge-drive config — `{enabled, url, headline, subline,
  // cta_label, frequency}`. When enabled, a "SUPPORT THE STATION"
  // interstitial randomly fires on channel changes at rate 1/frequency
  // (so `frequency: 10` averages one pledge every 10 tape switches).
  pledgeDrive,
  // ★ PATRON perk — hide on-screen ident distractions (top-left
  // channel-logo watermark + bottom-left SCARTLEAD.TV bezel wordmark).
  // Only meaningful when the viewer has unlocked the PATRON trophy;
  // PREFS gates the toggle so non-patrons never see the option.
  hideDistractions,
  onPower,
  children,
}) {
  // Phase 2.3.61g.1 — explicit CRT rendering profiles.
  // Full analogue ageing belongs to programme viewing, not interface text.
  // Every non-TV mode therefore gets a clean GUI-safe tube presentation.
  const isViewingProfile = mode === "tv";
  const isGuiProfile = power && !isViewingProfile;

  const tubeHaloRef = useRef(null);
  const ambilightTimerRef = useRef(null);
  const ambilightCanvasRef = useRef(null);
  const ambilightLastRef = useRef({
    top: [72, 190, 150], right: [72, 190, 150],
    bottom: [72, 190, 150], left: [72, 190, 150], level: 0.22,
  });
  const videoRef = useRef(null);
  // Phase 2.3.68-r1 — maintenance owns playback suspension for viewers.
  const maintenanceSuspendedRef = useRef(!!maintenanceSuspended);
  const maintenanceResumeRef = useRef({ native: false, youtube: false });
  maintenanceSuspendedRef.current = !!maintenanceSuspended;
  // Phase 2.3.61e.2-r4 — true DOM player handoff.
  // The authoritative media plane has one permanent React owner. The actual
  // DOM node is moved between its CRT "home" and an empty Guide preview host,
  // preserving the same <video>/iframe instance, playback position and session.
  const liveMediaHomeRef = useRef(null);
  const liveMediaPlaneRef = useRef(null);
  const [guidePreviewHost, setGuidePreviewHost] = useState(null);
  const screenRef = useRef(null);
  const megaWrapRef = useRef(null);
  const [progress, setProgress] = useState({ current: 0, duration: 0 });
  // Aspect ratio of the currently-playing HTML5 video, read from the
  // element's intrinsic videoWidth / videoHeight on `loadedmetadata`.
  // Drives the "smart fit" choice: tapes close to the CRT's native 4:3
  // get `object-cover` (filled corner-to-corner, classic broadcast feel);
  // wider tapes get `object-contain` (letterboxed, full frame visible).
  // null while metadata is loading — we default to cover during that
  // brief window since most tapes will fall into the cover bucket anyway.
  const [videoAspect, setVideoAspect] = useState(null);
  const [canvasSafePlayback, setCanvasSafePlayback] = useState(true);

  useEffect(() => {
    // Every new tape gets one canvas-safe playback attempt. If S4 refuses
    // CORS, onError below immediately falls back to the proven playback path.
    setCanvasSafePlayback(true);
  }, [currentVideo?.id]);


  // Phase 2.3.56r2b — broadcast-picture overlay geometry.
  // True broadcast graphics follow the programme picture; CRT/player OSD
  // remains fixed to the glass.
  const broadcastOverlayTransform = (() => {
    const iframePlane = currentVideo && (
      currentVideo.kind === "archive" || currentVideo.kind === "mega"
    );

    if (pictureMode === "14:9") {
      return iframePlane ? "scale(.86)" : "scale(.93)";
    }
    if (pictureMode === "cinema-zoom") {
      return "scale(1.14)";
    }
    if (pictureMode === "letterbox" && iframePlane) {
      return "scale(.75)";
    }
    if (pictureMode === "stretch" && iframePlane) {
      return "scaleX(.75) scaleY(1)";
    }
    return "none";
  })();
  const [showBar, setShowBar] = useState(false);

  const [showPictureOsd, setShowPictureOsd] = useState(false);
  const pictureOsdTimer = useRef(null);
  const lastPictureModeRef = useRef(pictureMode);

  useEffect(() => {
    if (lastPictureModeRef.current === pictureMode) return undefined;
    lastPictureModeRef.current = pictureMode;
    setShowPictureOsd(true);

    if (pictureOsdTimer.current) clearTimeout(pictureOsdTimer.current);
    pictureOsdTimer.current = setTimeout(() => {
      setShowPictureOsd(false);
      pictureOsdTimer.current = null;
    }, 1800);

    return () => {
      if (pictureOsdTimer.current) clearTimeout(pictureOsdTimer.current);
    };
  }, [pictureMode]);
  // Track playback paused-state for the seek-bar play/pause control.
  // Mirrored from the native <video> events for HTML5 tapes and from the
  // YouTube player state polling for YT tapes. Archive embeds have no JS
  // API, so the play/pause control is suppressed for them.
  const [isPaused, setIsPaused] = useState(false);
  const emitAnalyticsPlaying = React.useCallback((playing) => {
    try { window.dispatchEvent(new CustomEvent("crt:analytics-playing", { detail: { playing: !!playing } })); }
    catch (_) { /* non-critical */ }
  }, []);
  useEffect(() => () => emitAnalyticsPlaying(false), [emitAnalyticsPlaying]);
  const dvrLastVolumeRef = useRef(Math.max(1, Number(volume) || 5));
  const [dvrVolume, setDvrVolume] = useState(Math.max(0, Math.min(10, Number(volume) || 0)));
  const [dvrMuted, setDvrMuted] = useState((Number(volume) || 0) <= 0);
  const [synopsisExpanded, setSynopsisExpanded] = useState(false);
  // True while the viewer is actively dragging the seek thumb (pointer
  // down → up). We pause the visual progress poll during the drag so the
  // thumb doesn't fight the user's pointer when YT/HTML5 fires a partial
  // currentTime update mid-drag.
  const seekDragRef = useRef(false);
  const [seekHoverPct, setSeekHoverPct] = useState(null);
  // Phase 2.3.64.7 — one authoritative viewer-facing transport feedback surface.
  // Pause can hold it indefinitely; play/seek confirmations auto-dismiss.
  const [playbackFeedback, setPlaybackFeedback] = useState(null);
  const playbackFeedbackTimer = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showMobileFsExit, setShowMobileFsExit] = useState(false);
  const mobileFsExitTimer = useRef(null);
  const autoLandscapeFullscreenRef = useRef(false);
  const lastPhoneLandscapeRef = useRef(null);
  const [pwaLandscapeView, setPwaLandscapeView] = useState(false);
  const [showPwaLandscapeExit, setShowPwaLandscapeExit] = useState(false);
  const [showPwaFullscreenHint, setShowPwaFullscreenHint] = useState(false);
  const pwaFullscreenHintTimer = useRef(null);
  const pwaLandscapeDismissedRef = useRef(false);
  const [showPwaMediaBar, setShowPwaMediaBar] = useState(false);
  const [pwaMediaState, setPwaMediaState] = useState({ current: 0, duration: 0, paused: false, muted: false, volume: 1 });
  const pwaMediaHideRef = useRef(null);
  const [showFsHint, setShowFsHint] = useState(false);
  const hideTimer = useRef(null);
  const fsHintTimer = useRef(null);
  const [videoStarted, setVideoStarted] = useState(false);
  const [showBars, setShowBars] = useState(false);
  // (Test-card overlay removed entirely 2026-06-28 — owner request. The
  // boot sequence now goes channel-change static → SHOW STARTING SOON
  // countdown → tape, with no PBS-style ident card in the middle.)
  // Phase 2.3.64.3 — premium CRT power signature.
  const [warmingUp, setWarmingUp] = useState(false);
  const [shuttingDown, setShuttingDown] = useState(false);
  const prevPowerRef = useRef(power);
  const warmupTimerRef = useRef(null);
  const shutdownTimerRef = useRef(null);

  useEffect(() => {
    const wasPower = prevPowerRef.current;
    prevPowerRef.current = power;

    if (warmupTimerRef.current) {
      clearTimeout(warmupTimerRef.current);
      warmupTimerRef.current = null;
    }
    if (shutdownTimerRef.current) {
      clearTimeout(shutdownTimerRef.current);
      shutdownTimerRef.current = null;
    }

    if (power && wasPower === false) {
      setShuttingDown(false);
      if (reduceMotion || powerOnAnimation === false) {
        setWarmingUp(false);
        return undefined;
      }
      setWarmingUp(true);
      warmupTimerRef.current = setTimeout(() => {
        setWarmingUp(false);
        warmupTimerRef.current = null;
      }, 900);
      return () => {
        if (warmupTimerRef.current) {
          clearTimeout(warmupTimerRef.current);
          warmupTimerRef.current = null;
        }
      };
    }

    if (!power && wasPower === true) {
      setWarmingUp(false);
      if (reduceMotion || powerOnAnimation === false) {
        setShuttingDown(false);
        return undefined;
      }
      setShuttingDown(true);
      shutdownTimerRef.current = setTimeout(() => {
        setShuttingDown(false);
        shutdownTimerRef.current = null;
      }, 650);
      return () => {
        if (shutdownTimerRef.current) {
          clearTimeout(shutdownTimerRef.current);
          shutdownTimerRef.current = null;
        }
      };
    }

    return undefined;
  }, [power, reduceMotion, powerOnAnimation]);

  // ────────── Audio-reactive tube glow ──────────
  // When the viewer turns this on (PREFS → CRT TUBE GLOW → BREATHE WITH
  // AUDIO), we wire the playing <video> element into a Web Audio
  // analyser and read its RMS amplitude each animation frame. The
  // smoothed value drives the `--glow-pulse` CSS variable that the tube
  // glow rules already multiply into their box-shadow + vignette — so
  // loud audio swells the halo and quiet moments dim it.
  // Implementation notes:
  //   * `MediaElementAudioSourceNode` can only be created ONCE per
  //     element, so we cache via WeakMap keyed by the <video> node.
  //   * YouTube / Archive / MEGA tapes live inside cross-origin iframes
  //     and their audio can't be tapped via Web Audio API (browser
  //     security). For those we can't drive a real RMS pulse — instead
  //     the loop generates a subtle sine-wave "heartbeat" so the tube
  //     still feels alive rather than freezing at 1.0 while the tape
  //     plays. Frequency ~0.55Hz (chill breath), amplitude ±6% — well
  //     under the range of real audio reactivity (which can hit ±40%)
  //     so viewers can still tell the difference.
  //   * The analyser is a passthrough (source → analyser → destination)
  //     so audio still plays normally.
  //   * AudioContext starts SUSPENDED until a user gesture in many
  //     browsers; we call `ctx.resume()` defensively each effect run
  //     (cheap when already running). The viewer's POWER click satisfies
  //     the gesture so we're guaranteed a resumable context by the time
  //     this effect runs.
  const audioCtxRef = useRef(null);
  const audioSourceMapRef = useRef(null);
  const audioRafRef = useRef(null);
  // Mirror the live currentVideo kind + iframe-playing state into refs
  // so the audio-glow rAF loop can read them without needing to be torn
  // down and re-armed on every state change. `currentVideoKindRef`
  // holds the current tape kind (upload / mega / youtube / archive)
  // and `iframePlayingRef` is true only when a cross-origin iframe
  // tape is fully revealed AND not paused — the fake-heartbeat only
  // fires while both are true.
  const currentVideoKindRef = useRef(null);
  const iframePlayingRef = useRef(false);

  // Wire (or look up) the Web Audio chain for a given <video> element.
  // Chain: `source → gain → analyser → destination`. The same bundle is
  // reused for both the tube-glow analyser loop and the loudness-gain
  // effect — `createMediaElementSource` can only be called once per
  // element, so a shared WeakMap is required. Returns a `{source, gain,
  // analyser}` bundle or null on failure (no AudioContext, cross-origin
  // source, already bound by another component).
  const ensureAudioChain = useCallback((video) => {
    if (typeof window === "undefined" || !video) return null;
    if (!audioSourceMapRef.current) audioSourceMapRef.current = new WeakMap();
    let bundle = audioSourceMapRef.current.get(video);
    if (bundle) return bundle;
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    if (!audioCtxRef.current) {
      try { audioCtxRef.current = new Ctor(); } catch (e) { return null; }
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    try {
      const source = ctx.createMediaElementSource(video);
      const gain = ctx.createGain();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(gain);
      gain.connect(analyser);
      analyser.connect(ctx.destination);
      bundle = { source, gain, analyser };
      audioSourceMapRef.current.set(video, bundle);
    } catch (e) {
      return null;
    }
    return bundle;
  }, []);

  // Loudness normalisation — apply a per-video gain so a quiet old
  // cartoon and a loud blockbuster trailer play back at the same
  // perceived volume. Gain is computed from the delta between the
  // station's target LUFS (set by the owner in OwnerBar → AUDIO) and
  // the video's measured `lufs` (filled in by the backend ffmpeg
  // loudnorm pass). Clamped to [0.25, 3.0] so a wildly mismeasured
  // tape can't blow speakers or sound inaudible.
  useEffect(() => {
    if (!currentVideo || currentVideo.kind !== "upload") return;
    const video = videoRef.current;
    if (!video) return;
    const bundle = ensureAudioChain(video);
    if (!bundle) return;
    const target = (typeof audioTargetLufs === "number") ? audioTargetLufs : -16;
    const lufs = currentVideo.lufs;
    let g = 1;
    if (typeof lufs === "number" && Number.isFinite(lufs)) {
      g = Math.pow(10, (target - lufs) / 20);
      g = Math.max(0.25, Math.min(3.0, g));
    }
    try { bundle.gain.gain.value = g; } catch (e) { /* noop */ }
  }, [currentVideo, audioTargetLufs, ensureAudioChain]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!isViewingProfile || !tubeGlow || !tubeGlowAudioReactive || reduceMotion) {
      // When disabling/leaving TV, reset the halo pulse so GUI can never
      // inherit a stale reactive value.
      try { tubeHaloRef.current && tubeHaloRef.current.style.setProperty("--glow-pulse", "1"); } catch (e) { /* noop */ }
      return undefined;
    }
    let cancelled = false;
    let lastVideo = null;
    let analyser = null;
    let buf = null;
    let smoothed = 1;

    const tick = () => {
      if (cancelled) return;
      const video = screenRef.current && screenRef.current.querySelector("video");
      if (video !== lastVideo) {
        lastVideo = video;

        // Real Web Audio analysis is safe only for same-origin uploaded tapes.
        // Presigned S4 media must stay on the untouched native audio path:
        // createMediaElementSource() on a CORS-protected media element can
        // produce silence through the Web Audio graph in Chromium.
        const canAnalyseRealAudio =
          !!video && currentVideoKindRef.current === "upload";

        const bundle = canAnalyseRealAudio ? ensureAudioChain(video) : null;
        analyser = bundle ? bundle.analyser : null;
        buf = analyser ? new Uint8Array(analyser.fftSize) : null;
      }
      if (analyser && buf && lastVideo && !lastVideo.paused) {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);             // 0-1
        const target = 0.82 + Math.min(1, rms * 7) * 0.68;    // 0.85-1.4
        smoothed = smoothed * 0.82 + target * 0.18;
      } else if (lastVideo && !lastVideo.paused) {
        // S4/native protected playback: audio stays completely native.
        // React visually by driving --glow-pulse only. A two-frequency
        // waveform avoids looking like the regular BREATH animation.
        const t = performance.now() / 1000;
        const primary = Math.sin(t * 2 * Math.PI * 0.82) * 0.18;
        const secondary = Math.sin(t * 2 * Math.PI * 1.67) * 0.06;
        const target = 1 + primary + secondary;
        smoothed = smoothed * 0.72 + target * 0.28;
      } else if (
        // Cross-origin iframe source: real samples are unavailable, so
        // retain the existing gentler playback heartbeat.
        currentVideoKindRef.current &&
        iframePlayingRef.current
      ) {
        // ~0.55 Hz (one gentle breath every ~1.8s). Amplitude ±0.06
        // around a resting glow of 1.0 → tube pulses between 0.94 and
        // 1.06. Well below the ±0.4 swing of real audio-reactive so
        // there's still a clear "wow, this tape has real audio" feel
        // when the viewer switches to an uploaded tape.
        const t = performance.now() / 1000;
        const target = 1 + Math.sin(t * 2 * Math.PI * 0.55) * 0.06;
        smoothed = smoothed * 0.8 + target * 0.2;
      } else {
        // No analysable source (paused, between tapes, power off) —
        // gently return to the resting value so the glow doesn't lock
        // mid-pulse.
        smoothed = smoothed * 0.9 + 1 * 0.1;
      }
      try { tubeHaloRef.current && tubeHaloRef.current.style.setProperty("--glow-pulse", smoothed.toFixed(3)); } catch (e) { /* noop */ }
      audioRafRef.current = requestAnimationFrame(tick);
    };
    audioRafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (audioRafRef.current) cancelAnimationFrame(audioRafRef.current);
      try { tubeHaloRef.current && tubeHaloRef.current.style.setProperty("--glow-pulse", "1"); } catch (e) { /* noop */ }
    };
  }, [isViewingProfile, tubeGlow, tubeGlowAudioReactive, reduceMotion, ensureAudioChain]);


  // Phase 2.3.61g.2 — video-reactive CRT Ambilight.
  // Native video is sampled into a tiny 32x18 canvas four times per second.
  // Four edge bands are averaged independently and smoothed so the result
  // feels like reflected picture light, not a fast RGB gaming strip.
  useEffect(() => {
    // Phase 2.3.61h.8-r3 — DOM-authoritative Ambilight startup.
    // First-load React timing can commit both the halo and native video after
    // this effect begins, so never permanently capture either ref here.
    const resolveAmbilightHalo = () =>
      tubeHaloRef.current ||
      (typeof document !== "undefined"
        ? document.querySelector('[data-testid="crt-natural-tube-halo"]')
        : null);

    const resolveAmbilightVideo = () => {
      const plane = liveMediaPlaneRef.current;
      const screen = screenRef.current;
      return (plane && plane.querySelector("video")) ||
        (screen && screen.querySelector("video")) ||
        videoRef.current ||
        null;
    };

    const clearSampler = () => {
      if (ambilightTimerRef.current) {
        clearInterval(ambilightTimerRef.current);
        ambilightTimerRef.current = null;
      }
    };

    if (!isViewingProfile || !tubeGlow || !currentVideo ||
        !canvasSafePlayback ||
        currentVideo.kind === "youtube" || currentVideo.kind === "archive" ||
        currentVideo.kind === "mega") {
      clearSampler();
      const halo = resolveAmbilightHalo();
      if (halo) {
        halo.setAttribute("data-video-reactive", "fallback");
        halo.setAttribute(
          "data-video-reactive-reason",
          !isViewingProfile ? "not-viewing"
            : !tubeGlow ? "glow-disabled"
            : !currentVideo ? "no-video"
            : !canvasSafePlayback ? "playback-cors-fallback"
            : "cross-origin-iframe-source"
        );
      }
      return clearSampler;
    }

    if (!ambilightCanvasRef.current) {
      ambilightCanvasRef.current = document.createElement("canvas");
      ambilightCanvasRef.current.width = 32;
      ambilightCanvasRef.current.height = 18;
    }
    const canvas = ambilightCanvasRef.current;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return clearSampler;
    let samplingAllowed = true;

    const sampleBand = (data, x0, y0, x1, y1) => {
      let r=0,g=0,b=0,count=0,lum=0;
      for (let y=y0;y<y1;y+=1) for (let x=x0;x<x1;x+=1) {
        const i=(y*32+x)*4, pr=data[i], pg=data[i+1], pb=data[i+2];
        const pxLum=(0.2126*pr+0.7152*pg+0.0722*pb)/255;
        if (pxLum < 0.025) continue;
        r+=pr; g+=pg; b+=pb; lum+=pxLum; count+=1;
      }
      if (!count) return {rgb:[12,18,18],lum:0.015};
      r/=count; g/=count; b/=count; lum/=count;
      const mean=(r+g+b)/3, sat=1.16;
      return {rgb:[
        Math.max(0,Math.min(255,Math.round(mean+(r-mean)*sat))),
        Math.max(0,Math.min(255,Math.round(mean+(g-mean)*sat))),
        Math.max(0,Math.min(255,Math.round(mean+(b-mean)*sat)))
      ],lum};
    };
    const smoothRgb=(prev,next,a=0.18)=>prev.map((v,i)=>Math.round(v+(next[i]-v)*a));

    const tick=()=>{
      const halo = resolveAmbilightHalo();
      const video = resolveAmbilightVideo();

      if (!halo || !video) {
        if (halo) {
          halo.setAttribute("data-video-reactive", "fallback");
          halo.setAttribute("data-video-reactive-reason", "video-not-mounted");
        }
        return;
      }

      if (!samplingAllowed || video.readyState < 2 || video.paused) return;
      try {
        ctx.drawImage(video,0,0,32,18);
        const pixels=ctx.getImageData(0,0,32,18).data;
        const bands={
          top:sampleBand(pixels,2,0,30,5),
          right:sampleBand(pixels,27,2,32,16),
          bottom:sampleBand(pixels,2,13,30,18),
          left:sampleBand(pixels,0,2,5,16),
        };
        const prev=ambilightLastRef.current;
        const next={
          top:smoothRgb(prev.top,bands.top.rgb),
          right:smoothRgb(prev.right,bands.right.rgb),
          bottom:smoothRgb(prev.bottom,bands.bottom.rgb),
          left:smoothRgb(prev.left,bands.left.rgb),
          level:prev.level+(((bands.top.lum+bands.right.lum+bands.bottom.lum+bands.left.lum)/4)-prev.level)*0.14,
        };
        ambilightLastRef.current=next;
        const cssRgb=(rgb)=>`${rgb[0]} ${rgb[1]} ${rgb[2]}`;
        halo.style.setProperty("--ambilight-top",cssRgb(next.top));
        halo.style.setProperty("--ambilight-right",cssRgb(next.right));
        halo.style.setProperty("--ambilight-bottom",cssRgb(next.bottom));
        halo.style.setProperty("--ambilight-left",cssRgb(next.left));
        halo.style.setProperty("--ambilight-level",Math.max(0.24,Math.min(0.82,0.18 + next.level*0.92)).toFixed(3));
        halo.setAttribute("data-video-reactive","active");
        halo.setAttribute("data-video-reactive-reason","canvas-sampling-active");
        try {
          window.__sltvAmbilightStatus = {
            state: "ACTIVE",
            source: currentVideo?.kind || null,
            videoId: currentVideo?.id || null,
            level: Number(Math.max(0.24,Math.min(0.82,0.18 + next.level*0.92)).toFixed(3)),
            zones: {
              top: next.top,
              right: next.right,
              bottom: next.bottom,
              left: next.left,
            },
          };
        } catch (_) { /* diagnostics only */ }
      } catch (error) {
        samplingAllowed=false;
        halo.setAttribute("data-video-reactive","fallback");
        halo.setAttribute("data-video-reactive-reason","canvas-security-fallback");
        try {
          window.__sltvAmbilightStatus = {
            state: "CORS_FALLBACK",
            source: currentVideo?.kind || null,
            videoId: currentVideo?.id || null,
          };
        } catch (_) { /* diagnostics only */ }
        clearSampler();
      }
    };
    tick();
    ambilightTimerRef.current=setInterval(tick,250);
    return clearSampler;
  }, [isViewingProfile,tubeGlow,currentVideo?.id,currentVideo?.kind,canvasSafePlayback]);

  // For YouTube tapes only — drives the opacity fade-in so the pre-play poster
  // doesn't punch through. Goes true once the ident sequence (bars+testcard)
  // has finished masking the iframe.
  const [ytRevealed, setYtRevealed] = useState(false);
  // Holds the active YT.Player instance so we can destroy it cleanly on
  // channel-change. Also tracks the container DOM node the API mounts into.
  const ytPlayerRef = useRef(null);
  const ytContainerRef = useRef(null);

  // Maintenance is a suspension state, not a retune. Keep the same
  // channel/tape/player instance and resume only what maintenance paused.
  useEffect(() => {
    maintenanceSuspendedRef.current = !!maintenanceSuspended;

    const clearBroadcastSlates = () => {
      if (bannerInTimer.current) clearTimeout(bannerInTimer.current);
      if (bannerOutTimer.current) clearTimeout(bannerOutTimer.current);
      if (scheduleInTimer.current) clearTimeout(scheduleInTimer.current);
      if (scheduleOutTimer.current) clearTimeout(scheduleOutTimer.current);
      if (pauseBannerDismissTimer.current) clearTimeout(pauseBannerDismissTimer.current);
      setBannerStage("hidden");
      setScheduleStage("hidden");
      pauseBannerHeldRef.current = false;
      pauseBannerDismissTimer.current = null;
    };

    const pauseNativeIfPlaying = () => {
      const el = videoRef.current;
      if (!el) return;
      try {
        if (!el.paused && !el.ended) {
          maintenanceResumeRef.current.native = true;
          el.pause();
          setIsPaused(true);
        }
      } catch (e) { /* player may be between sources */ }
    };

    if (maintenanceSuspended) {
      clearBroadcastSlates();
      if (maintenanceWakeOnly) {
        maintenanceResumeRef.current.native = false;
        maintenanceResumeRef.current.youtube = false;
      }
      pauseNativeIfPlaying();
      const retryA = setTimeout(pauseNativeIfPlaying, 40);
      const retryB = setTimeout(pauseNativeIfPlaying, 180);

      try {
        const p = ytPlayerRef.current;
        if (p && typeof p.getPlayerState === "function") {
          const state = p.getPlayerState();
          if (state === 1 || state === 3) {
            maintenanceResumeRef.current.youtube = true;
            if (typeof p.pauseVideo === "function") p.pauseVideo();
            setIsPaused(true);
          }
        }
      } catch (e) { /* YT player may still be warming up */ }

      return () => {
        clearTimeout(retryA);
        clearTimeout(retryB);
      };
    }

    if (maintenanceResumeRef.current.native) {
      const el = videoRef.current;
      maintenanceResumeRef.current.native = false;
      if (el && el.paused && !el.ended) {
        try {
          const promise = el.play();
          if (promise && typeof promise.catch === "function") promise.catch(() => {});
        } catch (e) { /* browser autoplay policy */ }
      }
    }

    if (maintenanceResumeRef.current.youtube) {
      maintenanceResumeRef.current.youtube = false;
      try {
        const p = ytPlayerRef.current;
        if (p && typeof p.playVideo === "function") p.playVideo();
      } catch (e) { /* player may have changed */ }
    }

    return undefined;
  }, [maintenanceSuspended, currentVideo?.id]);

  // Mirror live tape state into refs consumed by the audio-glow rAF loop
  // (BREATHE WITH AUDIO pref). Refs let us keep the loop alive across
  // channel changes without tearing down / re-arming its raf state.
  //   * `currentVideoKindRef` — active tape kind (upload / mega / youtube
  //     / archive).
  //   * `iframePlayingRef`   — true only when a cross-origin iframe tape
  //     is up (`ytRevealed`) AND not paused; that's when the fake
  //     sine-wave heartbeat kicks in.
  useEffect(() => {
    currentVideoKindRef.current = currentVideo ? currentVideo.kind : null;
    emitAnalyticsPlaying(false);
  }, [currentVideo, emitAnalyticsPlaying]);
  useEffect(() => {
    iframePlayingRef.current = !!(ytRevealed && !isPaused);
  }, [ytRevealed, isPaused]);

  // Pledge-drive interstitial — rolls a random die once each tape has
  // ACTUALLY started (past the between-channel static) and, when it
  // lands, fires the SUPPORT THE STATION identity card over the tube.
  // Non-blocking (the tape keeps playing behind), just visually masks
  // the tube for ~4 seconds.
  //
  // Trigger rules:
  //   • Config must be `enabled` and carry a `url` — otherwise `null`
  //     comes back from the backend enabled getter and we skip.
  //   • Roll happens once per tape, only after the static/countdown
  //     ident has cleared (`!isStatic && power && mode === 'tv'`).
  //     A ref remembers which tape we last rolled for so subsequent
  //     re-renders in the same tape can't roll a second time.
  //   • Fires with probability 1/frequency on each roll (frequency
  //     defaults to 10 → averages one pledge every 10 tape switches).
  //   • Skips the very first tape after mount so cold viewers don't
  //     get hit with a pledge before watching anything.
  //
  // Previous version listened only on `currentVideo?.id` change and
  // therefore always evaluated during the isStatic=true window,
  // silently bailing out — the pledge card never fired for real
  // viewers even when the config was correct.
  const [pledgeShowing, setPledgeShowing] = useState(false);
  const pledgeSeenFirstTapeRef = useRef(false);
  const pledgeLastRolledIdRef = useRef(null);
  useEffect(() => {
    if (!currentVideo || !currentVideo.id) return;
    if (!power || isStatic || mode !== "tv") return;
    // Only roll once per tape — the effect re-runs when isStatic flips
    // false or pledgeDrive settings arrive, but we want a single roll
    // per tape id.
    if (pledgeLastRolledIdRef.current === currentVideo.id) return;
    pledgeLastRolledIdRef.current = currentVideo.id;
    // Skip the very first tape of the session (mount) so cold viewers
    // don't get hit with a pledge before they've watched anything.
    if (!pledgeSeenFirstTapeRef.current) {
      pledgeSeenFirstTapeRef.current = true;
      return;
    }
    if (!pledgeDrive || !pledgeDrive.enabled || !pledgeDrive.url) return;
    const freq = Math.max(2, Math.min(100, Number(pledgeDrive.frequency) || 10));
    if (Math.random() < 1 / freq) {
      setPledgeShowing(true);
    }
  }, [currentVideo?.id, isStatic, power, mode, pledgeDrive]);

  // Owner "PREVIEW NOW" hook — the OwnerBar dispatches this event when
  // the operator wants to see the pledge card without waiting for a
  // random trigger. Same visual sequence as an organic firing.
  useEffect(() => {
    const onPreview = () => setPledgeShowing(true);
    const onThanksPreview = () => setThanksShowing(true);
    window.addEventListener("crt:pledge-drive-preview", onPreview);
    window.addEventListener("crt:thanks-preview", onThanksPreview);
    return () => {
      window.removeEventListener("crt:pledge-drive-preview", onPreview);
      window.removeEventListener("crt:thanks-preview", onThanksPreview);
    };
  }, []);

  // ★ THANK YOU ★ bumper — fires when a viewer returns to the tab
  // after clicking the pledge-drive CTA. Cannot verify a real tip
  // (BMC doesn't ping us back), so we honor-system it: if the CTA
  // was clicked AND the viewer was away for at least 15 seconds
  // (long enough to plausibly have completed a payment), we trust
  // them, unlock the PATRON trophy and show the golden ident.
  //
  // Behaviour rules:
  //   • Only fires ONCE per viewer (cooldown flag) — repeated tips
  //     are welcome, but the ident spam wouldn't feel classy.
  //   • Requires a 15s minimum "away" window (guards against a
  //     mis-click that bounces the tab back instantly).
  //   • Caps the "away" window at 4 hours (avoid firing on a
  //     stale flag from a previous session where the viewer
  //     clicked but never actually completed the tip).
  //   • Respects the same power/tv gates as the pledge ident so
  //     it never overlays the maintenance slate.
  const [thanksShowing, setThanksShowing] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const MIN_AWAY_MS = 15 * 1000;
    const MAX_AWAY_MS = 4 * 60 * 60 * 1000;
    const check = () => {
      if (document.visibilityState !== "visible") return;
      let pendingAt = 0;
      let alreadyThanked = false;
      try {
        pendingAt = Number(localStorage.getItem("crt_tip_pending_at") || 0);
        alreadyThanked = !!localStorage.getItem("crt_tip_thanked_at");
      } catch (e) { return; }
      if (!pendingAt || alreadyThanked) return;
      const away = Date.now() - pendingAt;
      if (away < MIN_AWAY_MS || away > MAX_AWAY_MS) {
        // Too fast (misclick) OR too old (stale from a past session) —
        // clear the flag so it doesn't linger and eventually pop up
        // uninvited.
        try { localStorage.removeItem("crt_tip_pending_at"); } catch (e) { /* noop */ }
        return;
      }
      // Trust the return — clear both breadcrumbs and mark thanked so
      // we never repeat, then show the ident + unlock PATRON. The
      // trophy fire is idempotent inside the trophies lib so a stray
      // re-mount can't double-toast.
      try {
        localStorage.removeItem("crt_tip_pending_at");
        localStorage.setItem("crt_tip_thanked_at", String(Date.now()));
      } catch (e) { /* noop */ }
      setThanksShowing(true);
      if (fireTrophy) fireTrophy("patron");
      // Broadcast a trophy-unlocked event so passive UI (PatronBug pill
      // on the bezel) can light up immediately without a reload.
      try {
        window.dispatchEvent(new CustomEvent("crt:trophy-unlocked", { detail: { id: "patron" } }));
      } catch (e) { /* noop */ }
      // First-time patron unlock — auto-enable the "hide distractions"
      // perk once so tippers immediately see the clean-bezel/clean-tube
      // reward. A one-shot localStorage flag makes this idempotent: if
      // the viewer later toggles distractions BACK ON in PREFS, we
      // won't overwrite their preference on the next patron unlock.
      try {
        if (!localStorage.getItem("crt_patron_auto_hide_applied")) {
          patchPrefs({ hide_distractions: true });
          localStorage.setItem("crt_patron_auto_hide_applied", "1");
        }
      } catch (e) { /* noop */ }
    };
    // Check on mount (viewer might have returned before this effect
    // even attached) and on every visibility flip back to visible.
    check();
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    return () => {
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, [fireTrophy]);
  // "NOW PLAYING" channel banner — slides in from the left for ~3s on every
  // channel/tape change. State drives the CSS transform.
  const [bannerStage, setBannerStage] = useState("hidden"); // hidden | in | out
  const bannerInTimer = useRef(null);
  const bannerOutTimer = useRef(null);
  const [bannerClock, setBannerClock] = useState("");

  // Phase 2.3.60 — pause owns its own banner hold lifecycle.
  // This ref prevents ordinary PLAYING state changes (initial load, channel
  // tune, programme advance) from being mistaken for a pause-resume cycle.
  const pauseBannerHeldRef = useRef(false);
  const pauseBannerDismissTimer = useRef(null);
  // "NOW / NEXT / LATER" mini schedule strip — lives bottom-right, slides in
  // after the NOW BROADCASTING banner has cleared, hangs around long enough
  // to be read, then slides back out. Pure overlay; never blocks the video.
  const [scheduleStage, setScheduleStage] = useState("hidden"); // hidden | in | out
  const scheduleInTimer = useRef(null);
  const scheduleOutTimer = useRef(null);
  // Legacy tape/static machinery is retained for backwards compatibility,
  // but Phase 2.3.58 no longer uses it as the visible channel transition.
  const [showStatic, setShowStatic] = useState(false);
  const staticTimer = useRef(null);
  const staticCanvasRef = useRef(null);

  // Phase 2.3.58 — real tuner transition is keyed ONLY to channel identity.
  // A movie/episode advancing inside the same channel must remain seamless.
  const [channelTuneActive, setChannelTuneActive] = useState(false);
  const channelTuneTimer = useRef(null);
  const lastTunedChannelRef = useRef(currentChannel?.id || null);

  useEffect(() => {
    const nextChannelId = currentChannel?.id || null;
    const previousChannelId = lastTunedChannelRef.current;

    // Initial mount is not a channel change.
    if (previousChannelId === null) {
      lastTunedChannelRef.current = nextChannelId;
      return undefined;
    }

    // Same channel: tape/movie/episode change — deliberately no retune.
    if (nextChannelId === previousChannelId) return undefined;

    lastTunedChannelRef.current = nextChannelId;

    if (channelTuneTimer.current) clearTimeout(channelTuneTimer.current);

    if (reduceMotion) {
      setChannelTuneActive(false);
      return undefined;
    }

    setChannelTuneActive(true);
    channelTuneTimer.current = setTimeout(() => {
      setChannelTuneActive(false);
      channelTuneTimer.current = null;
    }, 420);

    return () => {
      if (channelTuneTimer.current) {
        clearTimeout(channelTuneTimer.current);
        channelTuneTimer.current = null;
      }
    };
  }, [currentChannel?.id, reduceMotion]);
  const barsTimer = useRef(null);
  // Resume-from-last-watched: throttle persistence to ~once / 5s, show a
  // brief "RESUMED FROM m:ss" overlay when we actually jumped forward.
  const lastSaveRef = useRef(0);
  const resumedAtRef = useRef(0);
  const [resumedAt, setResumedAt] = useState(0);
  const resumeOverlayTimer = useRef(null);
  // YouTube bot-detection handling. We mount the YT player muted (Chrome's
  // autoplay policy blocks unmuted autoplay before a user gesture, which we
  // used to misread as "YouTube is showing the bot wall"). A real bot wall
  // surfaces as an onError(150/101/100) — we catch that explicitly. Until
  // the viewer interacts with the page we leave the player muted and show
  // a discreet "click to enable sound" hint so the channel keeps rotating
  // without audio rather than seeming broken.
  const [ytBotGated, setYtBotGated] = useState(false);
  const [hasUserGesture, setHasUserGesture] = useState(false);
  const [showSoundOnFlash, setShowSoundOnFlash] = useState(false);
  const ytGateTimer = useRef(null);
  const ytPlayerStartedRef = useRef(false);
  const soundOnTimer = useRef(null);
  // Scanline-intensity OSD — briefly surfaces when the viewer cycles via
  // long-press POWER. Skip the very first render so the OSD doesn't fire
  // on initial mount.
  const [showScanlineOsd, setShowScanlineOsd] = useState(false);
  const scanlineOsdTimer = useRef(null);
  const lastScanlineLevelRef = useRef(scanlineLevel);
  // EPG peek overlay — driven by `peekTick` bump from App.js (long-press
  // SELECT). Auto-dismisses after 6s; can be tapped to dismiss early.
  const [showEpgPeek, setShowEpgPeek] = useState(false);
  const epgPeekTimer = useRef(null);
  // (Removed 2026-02-25: first-visit "REMOTE TIPS" cheat-sheet — owner
  // request. Long-press shortcuts are now self-documenting via SELECT
  // peek and the in-screen tip card.) Leaving the localStorage key in
  // place so previously-seen viewers don't get re-shown anything if it
  // ever comes back.

  // "WELCOME BACK, {NAME}" ident bumper — fires exactly once per signed-in
  // session, surfaced as a call-sign-style slate the first time CRT
  // renders with `authUser` set. Tracked by user_id so a sign-out → sign-in
  // with the same account replays the bumper (re-establishes presence),
  // while a tab refresh while still signed-in does NOT. Persisted via
  // sessionStorage so a hard reload starts fresh, but client-side
  // navigation does not. The bumper stays on screen until the viewer
  // explicitly clicks CONTINUE — no auto-dismiss — so a viewer who steps
  // away from the cabinet still gets greeted on their return.
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  // Guards against the effect re-firing across React 18 StrictMode
  // double-mounts AND across post-OAuth `authUser` object-reference
  // refreshes (when the same user_id arrives as a fresh object after
  // the profile fetch resolves). Without this, an upstream re-render
  // can flip showWelcomeBack between true and false on first load —
  // user-visible as a popup that briefly appears then "skips" itself.
  const welcomeShownForUid = useRef(null);
  const markWelcomeBackShown = (uid) => {
    try { if (uid) sessionStorage.setItem("crt_welcome_back_shown", uid); } catch (e) { /* noop */ }
  };
  const dismissWelcomeBack = () => {
    setShowWelcomeBack(false);
    markWelcomeBackShown(authUser && authUser.user_id);
    // Tell App / ChannelGuide the banner has been dismissed so the
    // guide-integrated copy disappears immediately. The standalone
    // CRT-internal overlay has been retired in favour of the guide
    // banner — see `crt:welcome-back-show` dispatch in the effect
    // below for the matching pair.
    try {
      window.dispatchEvent(new CustomEvent("crt:welcome-back-hide"));
    } catch (e) { /* noop */ }
  };
  useEffect(() => {
    // Listen for the dismiss event fired by the ChannelGuide's inline
    // welcome-back banner so CRT-local state + sessionStorage stay in
    // sync with the guide-rendered copy. Without this, a guide-side
    // CONTINUE click would clear the banner UI but `showWelcomeBack`
    // here would still be `true` and the banner would re-fire on the
    // next re-mount.
    const onAppDismiss = () => {
      setShowWelcomeBack(false);
      markWelcomeBackShown(authUser && authUser.user_id);
    };
    window.addEventListener("crt:welcome-back-dismiss", onAppDismiss);
    return () => window.removeEventListener("crt:welcome-back-dismiss", onAppDismiss);
  }, [authUser]);

  useEffect(() => {
    const uid = authUser && authUser.user_id;
    if (!uid) return undefined;
    // Already greeted this exact user_id in this lifecycle? Bail out so
    // a re-fire (StrictMode, auth refresh) can't toggle the visible
    // state. Once shown, only an explicit CONTINUE click hides it.
    if (welcomeShownForUid.current === uid) return undefined;
    let alreadyShown = false;
    try {
      alreadyShown = sessionStorage.getItem("crt_welcome_back_shown") === uid;
    } catch (e) { /* noop */ }
    if (alreadyShown) {
      welcomeShownForUid.current = uid;
      return undefined;
    }
    welcomeShownForUid.current = uid;
    setShowWelcomeBack(true);
    // Broadcast the welcome-back payload so the ChannelGuide can render
    // it as an inline banner at the top of its channel list. The guide
    // and the welcome-back used to layer on top of each other (both at
    // z-40) which left the bumper occluded by the guide on power-on —
    // integrating them into a single surface removes that whole class
    // of overlay-collision bugs. Subtitle is pre-interpolated here so
    // the guide doesn't need to know about token expansion.
    try {
      const rawSub = (welcomeMessage && welcomeMessage.subtitle) || "";
      const interpolatedSub = rawSub ? interpolateWelcome(rawSub, {
        viewerName: authUser.name,
        newCount: whatsNew && whatsNew.count,
        channelName,
        channelNumberLabel,
        callsign: callsign || "CRT-1986",
      }) : "";
      window.dispatchEvent(new CustomEvent("crt:welcome-back-show", {
        detail: {
          user_id: uid,
          name: authUser.name,
          callsign: callsign || "CRT-1986",
          subtitle: interpolatedSub,
          whatsNewCount: (whatsNew && whatsNew.count) || 0,
          channelName,
          channelNumberLabel,
        },
      }));
    } catch (e) { /* noop */ }
    return undefined;
  }, [authUser]);

  // Live lobby pub/sub — chat, reactions, presence + station state.
  // Keep this SINGLE global room connected for the lifetime of the open
  // site, including while CRT power is off, so maintenance can take over
  // every viewer immediately. `tunedChannelId` below remains power-gated,
  // so powered-off cabinets do not inflate per-channel viewer counts.
  const liveChannelId = "__lobby__";
  // Forward the cabinet's current tune-state into the lobby socket so
  // the server can maintain its live per-channel viewer-count snapshot.
  // We send the channel UUID (not number) so virtual / renumbered
  // channels stay identifiable. Disabled (null) drops the viewer from
  // all channel counts — happens when the CRT is off.
  const tunedChannelId = (power && currentChannel && currentChannel.id) ? currentChannel.id : null;
  const {
    chatMessages,
    pinned: chatPinned,
    viewerCount,
    channelCounts,
    nick: liveNick,
    isOwnerNick,
    status: liveStatus,
    sendChat,
    renameNick,
    pinMessage,
  } = useLive({ channelId: liveChannelId, enabled: !!liveChannelId, tunedChannel: tunedChannelId });

  // Re-broadcast the per-channel viewer counts up to App.js (which owns
  // the ChannelGuide) via a window CustomEvent. Avoids lifting the WS
  // hook out of CRT just so the guide can read this one snapshot.
  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent("crt:channel-counts", { detail: channelCounts || {} }));
    } catch (e) { /* noop */ }
  }, [channelCounts]);

  function playPreRollTone() {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      // VHS mechanical "ka-chunk" — a stack of two short low-frequency
      // sine thumps simulating the physical KA (~90 Hz) then CHUNK
      // (~60 Hz) of a VCR shuttle locking onto a new tape head. Sine
      // waves (was square) + halved gains keep the tactile cycle
      // feeling but read as warm wood-knock instead of buzzy click —
      // much friendlier on late-night ears.
      const kaOsc = ctx.createOscillator();
      const kaGain = ctx.createGain();
      kaOsc.type = "sine";
      kaOsc.frequency.setValueAtTime(95, now);
      kaOsc.frequency.exponentialRampToValueAtTime(58, now + 0.09);
      kaGain.gain.setValueAtTime(0.0001, now);
      kaGain.gain.linearRampToValueAtTime(0.10, now + 0.012);
      kaGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
      const kaFilter = ctx.createBiquadFilter();
      kaFilter.type = "lowpass";
      kaFilter.frequency.value = 320;
      kaOsc.connect(kaFilter); kaFilter.connect(kaGain); kaGain.connect(ctx.destination);
      kaOsc.start(now); kaOsc.stop(now + 0.15);
      // Second softer "chunk" at +60ms — the mechanical re-engage.
      const chOsc = ctx.createOscillator();
      const chGain = ctx.createGain();
      chOsc.type = "sine";
      chOsc.frequency.setValueAtTime(72, now + 0.06);
      chOsc.frequency.exponentialRampToValueAtTime(40, now + 0.16);
      chGain.gain.setValueAtTime(0.0001, now + 0.06);
      chGain.gain.linearRampToValueAtTime(0.07, now + 0.075);
      chGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
      const chFilter = ctx.createBiquadFilter();
      chFilter.type = "lowpass";
      chFilter.frequency.value = 220;
      chOsc.connect(chFilter); chFilter.connect(chGain); chGain.connect(ctx.destination);
      chOsc.start(now + 0.06); chOsc.stop(now + 0.23);

      // Channel-change static — ~130ms of filtered white noise, mimicking
      // the soft tape hiss when the tuner locks on. Highpass lowered
      // (2200 → 1500 Hz) so the texture reads as analogue warmth rather
      // than RF crackle; gain dropped (0.18 → 0.055) so it sits behind
      // the ka-chunk instead of cutting through it.
      const noiseBuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.15), ctx.sampleRate);
      const ch = noiseBuf.getChannelData(0);
      for (let i = 0; i < ch.length; i++) ch[i] = (Math.random() * 2 - 1) * 0.6;
      const noise = ctx.createBufferSource();
      noise.buffer = noiseBuf;
      const noiseFilter = ctx.createBiquadFilter();
      noiseFilter.type = "highpass";
      noiseFilter.frequency.value = 1500;
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0.0001, now);
      noiseGain.gain.linearRampToValueAtTime(0.055, now + 0.015);
      noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
      noise.connect(noiseFilter); noiseFilter.connect(noiseGain); noiseGain.connect(ctx.destination);
      noise.start(now); noise.stop(now + 0.16);

      // 1 kHz ident pip — kicks in once the static fades. Cut from a
      // sharp 0.05 peak to a gentle 0.022 swell with a longer ramp so
      // it whispers "lock confirmed" instead of beeping.
      osc.frequency.setValueAtTime(1000, now + 0.12);
      g.gain.setValueAtTime(0.0001, now + 0.12);
      g.gain.linearRampToValueAtTime(0.022, now + 0.2);
      g.gain.setValueAtTime(0.022, now + 0.48);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 0.62);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(now + 0.12);
      osc.stop(now + 0.65);
      setTimeout(() => ctx.close(), 800);
    } catch (e) { /* silent: audio is non-critical */ }
  }

  // Reset any lingering timers every time the user tunes to a new tape.
  // The video element fires onPlaying once buffered playback actually begins.
  useEffect(() => {
    if (barsTimer.current) clearTimeout(barsTimer.current);
    if (maintenanceSuspendedRef.current || !currentVideo || isStatic || mode !== "tv") return;
    setResumedAt(0);
    if (resumeOverlayTimer.current) clearTimeout(resumeOverlayTimer.current);
    lastSaveRef.current = 0;
    // Phase 2.3.58r2 — programme changes inside the current channel are
    // seamless. Channel tuning is owned exclusively by the channel-id
    // transition effect above; never fire legacy static/audio here.

    // Slide in the "NOW PLAYING" channel banner ~1s into the ident
    // and hold it for 2s before sliding back out. Use a local clock
    // snapshot so it looks like a broadcast slate.
    if (bannerInTimer.current) clearTimeout(bannerInTimer.current);
    if (bannerOutTimer.current) clearTimeout(bannerOutTimer.current);
    if (scheduleInTimer.current) clearTimeout(scheduleInTimer.current);
    if (scheduleOutTimer.current) clearTimeout(scheduleOutTimer.current);
    setBannerStage("hidden");
    setScheduleStage("hidden");
    const now = new Date();
    setBannerClock(
      `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`
    );
    // NOW BROADCASTING banner + NOW/NEXT/LATER schedule strip — kept on
    // identical timelines so the two slates feel like one cohesive
    // "broadcast lower-thirds" element. Both slide in 1s into the ident,
    // hold for 7s, then slide out in lock-step (500ms transition,
    // 600ms before fully unmounted).
    bannerInTimer.current = setTimeout(() => {
      setBannerStage("in");
      setScheduleStage("in");
      bannerOutTimer.current = setTimeout(() => {
        setBannerStage("out");
        setScheduleStage("out");
        setTimeout(() => {
          setBannerStage("hidden");
          setScheduleStage("hidden");
        }, 600);
      }, 7000);
    }, 1000);

    if (currentVideo.kind === "youtube" || currentVideo.kind === "archive" || currentVideo.kind === "mega") {
      // External-embed tapes (YouTube, archive.org, MEGA): no green countdown
      // (no `onPlaying` signal from an iframe) — reveal the iframe
      // immediately so any pre-play poster never flashes through.
      setVideoStarted(true); // skip the "starting soon" gate
      setYtRevealed(true);
      return () => {
        if (bannerInTimer.current) clearTimeout(bannerInTimer.current);
        if (bannerOutTimer.current) clearTimeout(bannerOutTimer.current);
        if (scheduleInTimer.current) clearTimeout(scheduleInTimer.current);
        if (scheduleOutTimer.current) clearTimeout(scheduleOutTimer.current);
      };
    }

    setVideoStarted(true);
    return () => {
      if (barsTimer.current) clearTimeout(barsTimer.current);
      if (bannerInTimer.current) clearTimeout(bannerInTimer.current);
      if (bannerOutTimer.current) clearTimeout(bannerOutTimer.current);
      if (scheduleInTimer.current) clearTimeout(scheduleInTimer.current);
      if (scheduleOutTimer.current) clearTimeout(scheduleOutTimer.current);
    };
  }, [currentVideo, isStatic, mode, reduceMotion]);

  useEffect(() => {
    const onFsChange = () => {
      const fs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(fs);
      if (fs) {
        try { localStorage.setItem("crt_fs_hint_dismissed", "1"); } catch (e) { /* noop */ }
        setShowFsHint(false);
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  // Phase 2.3.79d-r6.2 — fullscreen EXIT auto-hide.
  useEffect(() => {
    const clearExitTimer = () => {
      if (mobileFsExitTimer.current) {
        clearTimeout(mobileFsExitTimer.current);
        mobileFsExitTimer.current = null;
      }
    };

    if (!isFullscreen) {
      clearExitTimer();
      setShowMobileFsExit(false);
      return undefined;
    }

    const revealMobileFsExit = () => {
      setShowMobileFsExit(true);
      clearExitTimer();
      mobileFsExitTimer.current = setTimeout(() => {
        setShowMobileFsExit(false);
        mobileFsExitTimer.current = null;
      }, 3000);
    };

    revealMobileFsExit();
    document.addEventListener("pointerdown", revealMobileFsExit, true);
    document.addEventListener("pointermove", revealMobileFsExit, true);
    document.addEventListener("touchstart", revealMobileFsExit, { capture: true, passive: true });
    window.addEventListener("orientationchange", revealMobileFsExit);

    return () => {
      clearExitTimer();
      document.removeEventListener("pointerdown", revealMobileFsExit, true);
      document.removeEventListener("pointermove", revealMobileFsExit, true);
      document.removeEventListener("touchstart", revealMobileFsExit, true);
      window.removeEventListener("orientationchange", revealMobileFsExit);
    };
  }, [isFullscreen]);

  // Once-per-session user gesture detector. Chrome lifts the unmuted-autoplay
  // restriction after the viewer interacts with the page in any way. We capture
  // it here so the YT volume sync effect knows it's safe to un-mute, and so the
  // "click to enable sound" hint can dismiss itself. Critically, we also call
  // `unMute()` + `setVolume()` synchronously *inside* the gesture handler so
  // we're still inside the user-activation callstack — calling it later from
  // a useEffect is too detached for the YT iframe API to honour reliably.
  useEffect(() => {
    if (hasUserGesture) return undefined;
    const onGesture = () => {
      setHasUserGesture(true);
      try {
        const p = ytPlayerRef.current;
        if (p && volume > 0) {
          if (typeof p.unMute === "function") p.unMute();
          if (typeof p.setVolume === "function") {
            p.setVolume(Math.max(0, Math.min(100, Math.round((volume / 10) * 100))));
          }
          // Flash a tiny "SOUND ON" confirmation so the viewer knows
          // their click took hold — particularly useful on the headphones
          // / couch case where the video itself may be visually quiet.
          setShowSoundOnFlash(true);
          if (soundOnTimer.current) clearTimeout(soundOnTimer.current);
          soundOnTimer.current = setTimeout(() => setShowSoundOnFlash(false), 2500);
        }
      } catch (e) { /* player not ready or destroyed — volume effect will retry */ }
    };
    window.addEventListener("pointerdown", onGesture, { once: true });
    window.addEventListener("keydown", onGesture, { once: true });
    window.addEventListener("touchstart", onGesture, { once: true });
    return () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
      window.removeEventListener("touchstart", onGesture);
    };
  }, [hasUserGesture, volume]);

  // Paint random greyscale noise into the static canvas while showStatic is
  // true. ImageData is much faster than draw-rect per pixel.
  useEffect(() => {
    if (!showStatic) return undefined;
    const canvas = staticCanvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    const W = 160, H = 90; // small res — scaled up by CSS, gives chunky CRT pixels
    canvas.width = W; canvas.height = H;
    let raf;
    let cancelled = false;
    const draw = () => {
      if (cancelled) return;
      const img = ctx.createImageData(W, H);
      const data = img.data;
      for (let i = 0; i < data.length; i += 4) {
        const v = (Math.random() * 255) | 0;
        data[i] = v; data[i + 1] = v; data[i + 2] = v; data[i + 3] = 255;
      }
      ctx.putImageData(img, 0, 0);
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelled = true; if (raf) cancelAnimationFrame(raf); };
  }, [showStatic]);

  // Flush the current playback position to localStorage on tab close / hide.
  // Catches the case where the user navigates away mid-throttle-window so we
  // resume from "right now" instead of "5s ago".
  useEffect(() => {
    const flush = () => {
      const el = videoRef.current;
      if (!el || !currentVideo?.id || currentVideo.kind === "youtube" || currentVideo.kind === "archive" || currentVideo.kind === "mega") return;
      saveResumePosition(currentVideo.id, el.currentTime, el.duration || 0);
    };
    const onVis = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [currentVideo]);

  // YouTube player. Spins up the IFrame API for the current YT tape and
  // fires onVideoEnded when it finishes so App.js can rotate to the next
  // tape in the channel's playlist (matches the local <video> behaviour).
  useEffect(() => {
    if (!currentVideo || currentVideo.kind !== "youtube" || isStatic || (mode !== "tv" && mode !== "guide")) return;
    const container = ytContainerRef.current;
    if (!container) return;

    // ensureBroadcast still records `startedAt` for the live ticker / status
    // line, but we no longer seek by elapsed-time — playlists auto-advance
    // sequentially so each new tape starts fresh at t=0.
    ensureBroadcast(currentVideo.id);
    // Capture the seek-into-tape value at mount time. App.js drives this:
    // - 0 on the first tape ever (random pick) and after every auto-advance
    // - >0 only on zap-back, so the player resumes the position the tape
    //   would naturally be at right now (continuous broadcast feel).
    const seekAt = Math.max(0, Math.floor(initialSeekSec || 0));
    let player = null;
    let destroyed = false;

    // Each effect cycle gets a fresh DOM mount point — the YT API replaces
    // this element with the iframe it creates.
    const mountEl = document.createElement("div");
    mountEl.style.width = "100%";
    mountEl.style.height = "100%";
    container.innerHTML = "";
    container.appendChild(mountEl);

    // Snapshot of mute state at player-creation time. If the viewer has
    // already activated audio on a previous tape, the new player should
    // boot un-muted so they don't have to re-enable sound every channel
    // change. Chrome's autoplay policy treats the top-frame's user
    // activation as delegated to the YT iframe via the default
    // `allow="autoplay"` permission.
    const startMuted = !hasUserGesture || volume === 0;
    const startVolume = Math.max(0, Math.min(100, Math.round((volume / 10) * 100)));

    loadYouTubeApi()
      .then((YT) => {
        if (destroyed) return;
        ytPlayerStartedRef.current = false;
        player = new YT.Player(mountEl, {
          videoId: currentVideo.youtube_id,
          playerVars: {
            autoplay: maintenanceSuspendedRef.current ? 0 : 1,
            // Boot muted only when the viewer hasn't yet interacted with
            // the page (Chrome's autoplay policy will pause an unmuted
            // iframe otherwise). After the first gesture we trust the
            // delegated activation and start each new tape with sound.
            mute: startMuted ? 1 : 0,
            controls: 0,
            modestbranding: 1,
            rel: 0,
            iv_load_policy: 3,
            fs: 0,
            disablekb: 1,
            playsinline: 1,
            showinfo: 0,
            cc_load_policy: 0,
            start: seekAt,
            // No `loop`/`playlist` — we want a natural ENDED state on
            // onStateChange so the parent can advance to the next tape.
          },
          host: "https://www.youtube.com",
          events: {
            onReady: (e) => {
              try {
                const dur = e.target.getDuration();
                if (dur) recordDuration(currentVideo.id, dur);
                // YouTube respects `start:` for fresh loads, but if the
                // viewer zaps back to a tape that's still cached client-side,
                // the player sometimes ignores it. Re-seek defensively.
                if (seekAt > 0 && dur && seekAt < dur) {
                  try { e.target.seekTo(seekAt, true); } catch (err) { /* noop */ }
                }
                // Apply the snapshot mute state. Critically this matches
                // the playerVars above so we don't accidentally flip the
                // player into the opposite mode after onReady.
                try {
                  if (startMuted) {
                    e.target.mute();
                  } else {
                    e.target.unMute();
                    if (typeof e.target.setVolume === "function") {
                      e.target.setVolume(startVolume);
                    }
                  }
                } catch (err) { /* noop */ }
                if (maintenanceSuspendedRef.current) {
                  maintenanceResumeRef.current.youtube = !maintenanceWakeOnly;
                  try { e.target.pauseVideo(); } catch (err) { /* noop */ }
                  setIsPaused(true);
                } else {
                  e.target.playVideo();
                }
                // Bot-gate watchdog: if the player NEVER reaches PLAYING
                // within 12s, surface a friendly notice and auto-skip. We
                // start muted to bypass autoplay restrictions, so the only
                // remaining reason for not-playing is a genuine YouTube
                // block (bot wall / region lock / embed-disabled).
                ytGateTimer.current = setTimeout(() => {
                  const p = ytPlayerRef.current;
                  if (!p) return;
                  if (ytPlayerStartedRef.current) return;
                  try {
                    const st = typeof p.getPlayerState === "function" ? p.getPlayerState() : -1;
                    if (st !== 1 && st !== 3) {
                      setYtBotGated(true);
                      // Same public-flag path as the onError(101/150) case.
                      // A 12s "never started" is a strong embed-block signal
                      // for tapes whose iframe refuses silently without
                      // emitting an explicit error code.
                      if (currentVideo && currentVideo.kind === "youtube") {
                        try {
                          fetch(`${apiBase}/videos/${currentVideo.id}/embed-blocked`, {
                            method: "POST",
                          }).catch(() => { /* noop — best effort */ });
                        } catch (err) { /* noop */ }
                      }
                      setTimeout(() => {
                        setYtBotGated(false);
                        if (onVideoEnded) onVideoEnded();
                      }, 4000);
                    }
                  } catch (err) { /* noop */ }
                }, 12000);
              } catch (err) { /* API quirks shouldn't block playback */ }
            },
            onError: (e) => {
              // Codes 101/150 = embed disallowed; 100 = not found; 5 =
              // HTML5 player error. All of these are auto-skippable.
              const code = e && typeof e.data === "number" ? e.data : null;
              // Public report: any viewer's iframe firing onError on a
              // YouTube tape is a stronger signal than the nightly Data
              // API scan (which can return `embeddable: true` for tapes
              // that fail at runtime due to copyright/territory rules).
              // Backend rate-limits per-IP to deter scripted abuse.
              if (currentVideo && currentVideo.kind === "youtube" && (code === 101 || code === 150 || code === 100 || code === 5)) {
                try {
                  fetch(`${apiBase}/videos/${currentVideo.id}/embed-blocked`, {
                    method: "POST",
                  }).catch(() => { /* noop — best effort */ });
                } catch (err) { /* noop */ }
              }
              // Viewer preference: skip the 4s "SIGNAL BLOCKED" overlay and
              // tune immediately. Keeps the wall-of-shame off the screen
              // for viewers who don't care which tape was refused.
              if (silentAutoSkip) {
                if (onVideoEnded) onVideoEnded();
                return;
              }
              setYtBotGated(true);
              setTimeout(() => {
                setYtBotGated(false);
                if (onVideoEnded) onVideoEnded();
              }, 4000);
            },
            onStateChange: (e) => {
              if (e.data === 1) emitAnalyticsPlaying(true);
              else if (e.data === 0 || e.data === 2 || e.data === 5) emitAnalyticsPlaying(false);
              // PLAYING — maintenance is an absolute transport gate.
              if (e.data === 1 && maintenanceSuspendedRef.current) {
                try { e.target.pauseVideo(); } catch (_) {}
                setIsPaused(true);
                return;
              }
              // Normal PLAYING — clear the bot-gate watchdog since we got through.
              if (e.data === 1) {
                ytPlayerStartedRef.current = true;
                if (ytGateTimer.current) {
                  clearTimeout(ytGateTimer.current);
                  ytGateTimer.current = null;
                }
              }
              // State 0 = ENDED. Auto-advance to the next tape — this is
              // what makes the channel "rotate" instead of repeating one
              // video forever.
              if (e.data === 0 && onVideoEnded) {
                onVideoEnded();
              }
            },
          },
        });
        ytPlayerRef.current = player;
      })
      .catch(() => { /* API failed to load — fall back is just a blank player */ });

    return () => {
      destroyed = true;
      if (ytGateTimer.current) {
        clearTimeout(ytGateTimer.current);
        ytGateTimer.current = null;
      }
      try {
        if (player && typeof player.destroy === "function") player.destroy();
      } catch (e) { /* noop */ }
      ytPlayerRef.current = null;
      if (container) container.innerHTML = "";
    };
  }, [currentVideo, isStatic, mode, onVideoEnded]);

  // Poll the YT player's current time once a second so the LIVE badge inside
  // the NOW BROADCASTING banner ticks. Local <video> already updates the
  // same `progress` state via its onTimeUpdate handler; this effect mirrors
  // that for the YT case where we don't get free time-update events.
  useEffect(() => {
    if (maintenanceSuspendedRef.current || !currentVideo || currentVideo.kind !== "youtube" || isStatic || (mode !== "tv" && mode !== "guide")) return;
    const id = setInterval(() => {
      const p = ytPlayerRef.current;
      if (!p || typeof p.getCurrentTime !== "function") return;
      try {
        const t = p.getCurrentTime() || 0;
        const d = p.getDuration() || 0;
        if (!seekDragRef.current) {
          setProgress({ current: t, duration: d });
        }
        // YT.PlayerState: 1 = playing, 2 = paused. Anything else
        // (buffering/cued/ended) we leave the last known value to avoid
        // flicker during seek/buffer transitions.
        if (typeof p.getPlayerState === "function") {
          const s = p.getPlayerState();
          if (s === 1) setIsPaused(false);
          else if (s === 2) setIsPaused(true);
        }
      } catch (e) { /* player still warming up */ }
    }, 1000);
    return () => clearInterval(id);
  }, [currentVideo, isStatic, mode]);

  // SELECT button — re-summon the NOW BROADCASTING banner + COMING UP strip
  // on demand without going through a channel-change sequence (no bars / no
  // test card). Each bump of `selectTick` from App.js triggers a single
  // fresh reveal. Skip the very first render (selectTick = 0 = initial
  // state) so the banner doesn't double-fire alongside the channel-change
  // effect. Banner and schedule slide in/out together on the same timeline
  // for visual coherence with the channel-change reveal.
  useEffect(() => {
    if (maintenanceSuspendedRef.current || !selectTick || !currentVideo || mode !== "tv" || isStatic) return;
    if (bannerInTimer.current) clearTimeout(bannerInTimer.current);
    if (bannerOutTimer.current) clearTimeout(bannerOutTimer.current);
    setBannerStage("hidden");
    setScheduleStage("hidden");
    const now = new Date();
    setBannerClock(
      `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`
    );
    // Short cold delay (no bars to wait for) — both slates land together
    // ~100ms after the press, hold for 7s, then slide out together.
    bannerInTimer.current = setTimeout(() => {
      setBannerStage("in");
      setScheduleStage("in");
      bannerOutTimer.current = setTimeout(() => {
        setBannerStage("out");
        setScheduleStage("out");
        setTimeout(() => {
          setBannerStage("hidden");
          setScheduleStage("hidden");
        }, 600);
      }, 7000);
    }, 100);
  }, [selectTick]);

  // Phase 2.3.62.6-r5 - same-channel programme-boundary reveal.
  // Reuses only NOW BROADCASTING + COMING UP; never tuner static or channel OSD.
  useEffect(() => {
    if (maintenanceSuspendedRef.current || !programmeHandoffTick || !currentVideo || mode !== "tv" || isStatic) return undefined;

    if (bannerInTimer.current) clearTimeout(bannerInTimer.current);
    if (bannerOutTimer.current) clearTimeout(bannerOutTimer.current);

    setBannerStage("hidden");
    setScheduleStage("hidden");

    const now = new Date();
    setBannerClock(
      `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`
    );

    bannerInTimer.current = setTimeout(() => {
      setBannerStage("in");
      setScheduleStage("in");
      bannerOutTimer.current = setTimeout(() => {
        setBannerStage("out");
        setScheduleStage("out");
        setTimeout(() => {
          setBannerStage("hidden");
          setScheduleStage("hidden");
        }, 600);
      }, 5000);
    }, 100);

    return undefined;
  }, [programmeHandoffTick]);

  // Scanline-level OSD trigger. Compares the current level to a ref so we
  // only flash when the viewer actively cycled (skips initial mount).
  useEffect(() => {
    if (lastScanlineLevelRef.current === scanlineLevel) return undefined;
    lastScanlineLevelRef.current = scanlineLevel;
    setShowScanlineOsd(true);
    if (scanlineOsdTimer.current) clearTimeout(scanlineOsdTimer.current);
    scanlineOsdTimer.current = setTimeout(() => setShowScanlineOsd(false), 1800);
    return undefined;
  }, [scanlineLevel]);

  // First-visit cheat-sheet auto-dismiss watchdog. Holds the cheat-sheet
  // (Removed 2026-02-25 alongside the cheatsheet JSX — owner request.)

  // EPG peek effect — opens the overlay on every `peekTick` bump and
  // auto-dismisses after 6s. Skips the initial render (tick = 0).
  useEffect(() => {
    if (!peekTick) return undefined;
    setShowEpgPeek(true);
    if (epgPeekTimer.current) clearTimeout(epgPeekTimer.current);
    epgPeekTimer.current = setTimeout(() => setShowEpgPeek(false), 6000);
    return undefined;
  }, [peekTick]);

  // Show the FS hint briefly when a new video starts, unless user has already
  // gone fullscreen at least once on this device.
  // (Fullscreen disabled by owner request — effect retained as a no-op to
  // keep the dependency array stable; hint state simply stays false.)
  useEffect(() => {}, [currentVideo, mode]);

  useEffect(() => {
    const onGuidePreviewHost = (e) => {
      setGuidePreviewHost(e?.detail?.host || null);
    };
    window.addEventListener("crt:guide-preview-host", onGuidePreviewHost);
    return () => window.removeEventListener("crt:guide-preview-host", onGuidePreviewHost);
  }, []);

  useEffect(() => {
    const plane = liveMediaPlaneRef.current;
    const home = liveMediaHomeRef.current;
    if (!plane || !home) return;

    const target = mode === "guide" && guidePreviewHost ? guidePreviewHost : home;
    if (plane.parentNode !== target) {
      target.appendChild(plane);
    }

    plane.style.position = "absolute";
    plane.style.inset = "0";
    plane.style.width = "100%";
    plane.style.height = "100%";
    plane.style.zIndex = mode === "guide" ? "10" : "0";
    plane.style.pointerEvents = "none";
    plane.style.transform = "none";

    return () => {
      // Never strand the live player inside a Guide host that is about to
      // unmount. Returning it home preserves React ownership and playback.
      if (plane && home && plane.parentNode !== home) {
        try { home.appendChild(plane); } catch (e) { /* host already gone */ }
      }
    };
  }, [mode, guidePreviewHost]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = Math.max(0, Math.min(1, volume / 10));
    }
    // Mirror volume into the YT.Player (0-100) so the remote's VOL ▲▼ works
    // on YouTube tapes too. Wrapped in try/catch because the player may not
    // be ready yet on the very first mount tick. We only call unMute() once
    // the viewer has interacted with the page — un-muting before a user
    // gesture would let Chrome's autoplay policy pause the iframe, which
    // would then trip the bot-gate watchdog (the bug we just fixed).
    try {
      const p = ytPlayerRef.current;
      if (p && typeof p.setVolume === "function") {
        p.setVolume(Math.max(0, Math.min(100, Math.round((volume / 10) * 100))));
        if (volume === 0 || !hasUserGesture) {
          if (typeof p.mute === "function") p.mute();
        } else if (typeof p.unMute === "function") {
          p.unMute();
        }
      }
    } catch (e) { /* player still warming up */ }
  }, [volume, currentVideo, hasUserGesture]);

  // Phase 2.3.62.3-r3-r2 — conservative native MEGA S4 drift correction.
  // App.js refreshes initialSeekSec from the authoritative clock every 60s.
  // Differences below 3s are observed but intentionally left alone.
  useEffect(() => {
    if (!liveTuneActive || !currentVideo || currentVideo.kind !== "mega_s4") return;

    const el = videoRef.current;
    if (!el || el.readyState < 1) return;

    const target = Number(initialSeekSec);
    if (!Number.isFinite(target) || target < 0) return;

    const duration = Number(el.duration) || Number(currentVideo.duration_sec) || 0;
    if (duration > 0 && target >= duration) return;

    const actual = Number(el.currentTime) || 0;
    const drift = actual - target;
    const absDrift = Math.abs(drift);

    if (absDrift >= 3) {
      try {
        el.currentTime = target;
        setProgress({ current: target, duration: duration || el.duration || 0 });
      } catch (_) {}

      try {
        window.__sltvBroadcastDriftStatus = {
          state: "CORRECTED",
          videoId: currentVideo.id,
          targetSec: target,
          actualBeforeSec: actual,
          driftSec: drift,
          thresholdSec: 3,
          at: new Date().toISOString(),
        };
      } catch (_) {}
    } else {
      try {
        window.__sltvBroadcastDriftStatus = {
          state: "IN_SYNC",
          videoId: currentVideo.id,
          targetSec: target,
          actualSec: actual,
          driftSec: drift,
          thresholdSec: 3,
          at: new Date().toISOString(),
        };
      } catch (_) {}
    }
  }, [liveTuneActive, initialSeekSec, currentVideo?.id]);

  useEffect(() => {
    if (videoRef.current && currentVideo) {
      // Phase 2.3.61h.5 — establish SCARTLEAD volume before media startup.
      // Never allow a newly-mounted native <video> to begin at the browser's
      // implicit 1.0 volume while waiting for the separate sync effect.
      videoRef.current.volume = Math.max(0, Math.min(1, volume / 10));
      if (maintenanceSuspendedRef.current) {
        try { videoRef.current.pause(); } catch (_) {}
        setIsPaused(true);
      } else {
        videoRef.current.load();
        const playPromise = videoRef.current.play();
        if (playPromise && playPromise.catch) {
          playPromise.catch(() => {});
        }
      }
    }
    // Clear any leftover progress from the previous tape so the seek bar
    // doesn't briefly show a stale duration on tape change (especially
    // visible when channelling from an HTML5/YT tape to an archive embed,
    // whose duration we can't read at all).
    setProgress({
      current: liveTuneActive ? Math.max(0, Number(initialSeekSec) || 0) : 0,
      duration: 0,
    });
    setIsPaused(maintenanceSuspendedRef.current);
    if (playbackFeedbackTimer.current) {
      clearTimeout(playbackFeedbackTimer.current);
      playbackFeedbackTimer.current = null;
    }
    setPlaybackFeedback(null);
    // Reset the cached aspect ratio so we don't show last tape's letterbox
    // bars while the next tape's metadata is still loading.
    setVideoAspect(null);
  }, [currentVideo]);

  const bumpBar = () => {
    setShowBar(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowBar(false), 2500);
  };

  const showPlaybackFeedback = (kind, label, seconds = null, hold = false) => {
    if (playbackFeedbackTimer.current) {
      clearTimeout(playbackFeedbackTimer.current);
      playbackFeedbackTimer.current = null;
    }
    const n = Number(seconds);
    setPlaybackFeedback({
      kind,
      label,
      seconds: Number.isFinite(n) ? Math.max(0, n) : null,
    });
    if (!hold) {
      playbackFeedbackTimer.current = setTimeout(() => {
        setPlaybackFeedback(null);
        playbackFeedbackTimer.current = null;
      }, reduceMotion ? 700 : 1100);
    }
  };

  // Compute the seek target (in seconds) from a pointer event over the bar.
  const seekTargetFromEvent = (e, rect) => {
    const r = rect || e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    return { pct, target: pct * progress.duration };
  };

  // Apply a seek to whichever player is active. Returns true if it ran.
  const applySeek = (seconds) => {
    if (!progress.duration) return false;
    const t = Math.max(0, Math.min(progress.duration, seconds));
    if (currentVideo?.kind === "youtube") {
      const p = ytPlayerRef.current;
      if (p && typeof p.seekTo === "function") {
        try { p.seekTo(t, true); } catch (e) { /* noop */ }
        setProgress((prev) => ({ ...prev, current: t }));
        return true;
      }
      return false;
    }
    const el = videoRef.current;
    if (!el) return false;
    el.currentTime = t;
    setProgress((prev) => ({ ...prev, current: t }));
    return true;
  };

  // ← / → keyboard shortcuts in App.js dispatch `crt:seek-relative`
  // with detail = signed second offset (-10 / +10). We handle it here
  // rather than in App because only CRT knows which player (YT / native
  // / MEGA / archive) is currently active and how to seek it. MEGA +
  // archive.org iframes have no JS-controllable seek, so we bump the
  // OSD to acknowledge the press instead of silently doing nothing.
  useEffect(() => {
    const onSeekRelative = (e) => {
      const delta = Number(e.detail);
      if (!Number.isFinite(delta)) return;
      // MEGA + archive.org tapes render inside iframes we can't
      // JS-seek. Their own iframes have native keyboard bindings that
      // handle arrows when the iframe has focus, so we silently ignore
      // the event here — attempting an OSD "acknowledge" via bumpBar
      // was misleading (bumpBar only drives the YT-specific overlay).
      if (currentVideo?.kind === "mega" || currentVideo?.kind === "archive") return;
      const now = progress.current || 0;
      const target = Math.max(0, Math.min(progress.duration || 0, now + delta));
      if (applySeek(target)) {
        bumpBar();
        showPlaybackFeedback(
          delta < 0 ? "rewind" : "forward",
          delta < 0 ? `REWIND ${Math.abs(delta)} SEC` : `FORWARD ${Math.abs(delta)} SEC`,
          target
        );
      }
    };
    window.addEventListener("crt:seek-relative", onSeekRelative);
    return () => window.removeEventListener("crt:seek-relative", onSeekRelative);
  }, [progress.current, progress.duration, currentVideo && currentVideo.kind]);

  // Toggle play/pause on whichever player is active. YT exposes
  // playVideo/pauseVideo; native <video> uses .play()/.pause(). MEGA
  // and archive.org have no JS-controllable player. Their transport
  // buttons are suppressed in the CRT UI so we do not imply that a
  // remote Play press can start a cross-origin iframe. MEGA remains
  // directly clickable and shows a "CLICK SCREEN TO PLAY" instruction.
  const togglePlayPause = () => {
    if (currentVideo?.kind === "mega" || currentVideo?.kind === "archive") {
      // External embeds with no JS-controllable player. Bump the OSD
      // so the viewer sees the button registered even if nothing
      // visibly changes; MEGA / archive.org have their own controls.
      bumpBar();
      return;
    }
    if (currentVideo?.kind === "youtube") {
      const p = ytPlayerRef.current;
      if (!p) return;
      try {
        const state = typeof p.getPlayerState === "function" ? p.getPlayerState() : null;
        // YT.PlayerState.PLAYING = 1
        if (state === 1) { p.pauseVideo(); setIsPaused(true); }
        else { p.playVideo(); setIsPaused(false); }
      } catch (e) { /* noop */ }
      bumpBar();
      return;
    }
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      const pr = el.play();
      if (pr && pr.catch) pr.catch(() => {});
    } else {
      el.pause();
    }
    bumpBar();
  };

  // Phase 2.3.51 — NOW BROADCASTING DVR control helpers. These all
  // operate the same live player state used by the existing seek bar / remote,
  // so the broadcast slate is a real transport surface rather than a second UI.
  const canControlTransport = currentVideo?.kind !== "archive" && currentVideo?.kind !== "mega";
  const canSeekTransport = canControlTransport && progress.duration > 0;

  const dvrSeekRelative = (delta) => {
    if (!canSeekTransport) return;
    const target = Math.max(0, Math.min(progress.duration || 0, (progress.current || 0) + delta));
    if (applySeek(target)) {
      bumpBar();
      showPlaybackFeedback(
        delta < 0 ? "rewind" : "forward",
        delta < 0 ? `REWIND ${Math.abs(delta)} SEC` : `FORWARD ${Math.abs(delta)} SEC`,
        target
      );
    }
  };

  // Phase 2.3.51c — DVR volume is controlled directly on the live player.
  // It deliberately does NOT call App.js onVolumeChange/setVolume, so a DVR
  // volume click cannot trigger the parent render/effect chain while video plays.
  useEffect(() => {
    setSynopsisExpanded(false);
  }, [currentVideo?.id]);

  const dvrApplyDirectVolume = (next, muted = false) => {
    const n = Math.max(0, Math.min(10, Number(next)));
    if (!Number.isFinite(n)) return;

    const el = videoRef.current;
    if (el) {
      try {
        el.volume = n / 10;
        el.muted = !!muted;
      } catch (_) { /* native player may be between tapes */ }
    }

    // YouTube remains directly controllable too, without App state.
    try {
      const p = ytPlayerRef.current;
      if (p && typeof p.setVolume === "function") {
        p.setVolume(Math.round(n * 10));
        if (muted || n === 0) {
          if (typeof p.mute === "function") p.mute();
        } else if (hasUserGesture && typeof p.unMute === "function") {
          p.unMute();
        }
      }
    } catch (_) { /* player still warming up */ }

    setDvrVolume(n);
    setDvrMuted(!!muted || n === 0);
    if (n > 0) dvrLastVolumeRef.current = n;

    // Phase 2.3.61h.5-r2 — keep App.js as the single persistent volume owner.
    // The direct player write above remains immediate, but the resulting value
    // is also reported upstream so the existing App persistence path saves it.
    if (typeof onVolumeChange === "function") {
      onVolumeChange((muted || n === 0) ? 0 : n);
    }
  };

  const dvrAdjustVolume = (delta) => {
    const base = dvrMuted ? 0 : dvrVolume;
    const next = Math.max(0, Math.min(10, base + delta));
    dvrApplyDirectVolume(next, false);
  };

  const dvrToggleMute = () => {
    if (!dvrMuted && dvrVolume > 0) {
      dvrLastVolumeRef.current = dvrVolume;
      dvrApplyDirectVolume(dvrVolume, true);
    } else {
      const restore = Math.max(1, Math.min(10, Number(dvrLastVolumeRef.current) || 5));
      dvrApplyDirectVolume(restore, false);
    }
  };

  useEffect(() => {
    const n = Math.max(0, Math.min(10, Number(volume) || 0));
    setDvrVolume(n);
    setDvrMuted(n === 0);
    if (n > 0) dvrLastVolumeRef.current = n;
  }, [volume]);

  const dvrStepTape = (dir) => {
    if (!Array.isArray(playlist) || playlist.length < 2 || typeof onStepVideo !== "function") return;
    onStepVideo(dir);
  };

  // Phase 2.3.79g-r2 — installed PWA landscape television mode.
  // Standalone phones can use the whole app viewport without relying on a
  // browser fullscreen permission grant. EXIT dismisses this view for the
  // current landscape orientation; portrait resets the dismissal.
  useEffect(() => {
    let settleTimer = null;
    let hideTimer = null;

    const standalone = () =>
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true;

    const phoneLandscape = () => {
      const w = Math.max(0, Number(window.innerWidth) || 0);
      const h = Math.max(0, Number(window.innerHeight) || 0);
      if (!w || !h) return false;
      return w > h && Math.min(w, h) <= 640 && Math.max(w, h) <= 1024;
    };

    const setRootMode = (active) => {
      document.documentElement.classList.toggle("sltv-pwa-landscape-view", !!active);
    };

    const scheduleExitHide = () => {
      if (hideTimer) clearTimeout(hideTimer);
      setShowPwaLandscapeExit(true);
      hideTimer = setTimeout(() => setShowPwaLandscapeExit(false), 3000);
    };

    const syncPwaLandscape = () => {
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        const landscape = phoneLandscape();
        if (!landscape) pwaLandscapeDismissedRef.current = false;
        // Phase 2.3.91r1 — landscape presentation belongs to the CRT,
        // not to its power/playback state. A powered-off television should
        // occupy the same phone-landscape presentation as a powered-on one.
        const active =
          standalone() && landscape && !pwaLandscapeDismissedRef.current;
        setPwaLandscapeView(active);
        setRootMode(active);
        if (active) scheduleExitHide();
        else setShowPwaLandscapeExit(false);
      }, 120);
    };

    const revealExit = () => {
      if (!document.documentElement.classList.contains("sltv-pwa-landscape-view")) return;
      scheduleExitHide();
    };

    syncPwaLandscape();
    const transitionOrientation = () => {
      document.documentElement.classList.add("sltv-pwa-orientation-transition");
      syncPwaLandscape();
      window.setTimeout(() => document.documentElement.classList.remove("sltv-pwa-orientation-transition"), 560);
    };

    window.addEventListener("orientationchange", transitionOrientation);
    window.addEventListener("resize", syncPwaLandscape);
    window.addEventListener("pointerdown", revealExit, { passive: true });
    window.addEventListener("touchstart", revealExit, { passive: true });

    return () => {
      if (settleTimer) clearTimeout(settleTimer);
      if (hideTimer) clearTimeout(hideTimer);
      setRootMode(false);
      document.documentElement.classList.remove("sltv-pwa-orientation-transition");
      window.removeEventListener("orientationchange", transitionOrientation);
      window.removeEventListener("resize", syncPwaLandscape);
      window.removeEventListener("pointerdown", revealExit);
      window.removeEventListener("touchstart", revealExit);
    };
  }, [power, mode, currentVideo]);

  // Phase 2.3.79g-r3.3 — touch television media bar.
  const pwaMediaElement = () => {
    const screen = document.querySelector(".crt-screen");
    return screen ? screen.querySelector("video, audio") : null;
  };

  const refreshPwaMediaState = () => {
    const media = pwaMediaElement();
    if (!media) return;
    setPwaMediaState({
      current: Number.isFinite(media.currentTime) ? media.currentTime : 0,
      duration: Number.isFinite(media.duration) ? media.duration : 0,
      paused: !!media.paused,
      muted: !!media.muted,
      volume: Number.isFinite(media.volume) ? media.volume : 1,
    });
  };

  const schedulePwaMediaHide = () => {
    if (pwaMediaHideRef.current) clearTimeout(pwaMediaHideRef.current);
    pwaMediaHideRef.current = null;
    const media = pwaMediaElement();
    if (!media || media.paused) return;
    pwaMediaHideRef.current = setTimeout(() => setShowPwaMediaBar(false), 3000);
  };

  const revealPwaMediaBar = () => {
    if (!pwaLandscapeView && !isFullscreen) return;
    refreshPwaMediaState();
    setShowPwaMediaBar(true);
    setShowPwaLandscapeExit(true);
    schedulePwaMediaHide();
  };

  const keepPwaMediaBarAlive = () => {
    setShowPwaMediaBar(true);
    schedulePwaMediaHide();
  };

  useEffect(() => {
    if (!pwaLandscapeView && !isFullscreen) {
      setShowPwaMediaBar(false);
      if (pwaMediaHideRef.current) clearTimeout(pwaMediaHideRef.current);
      return undefined;
    }
    const screen = document.querySelector(".crt-screen");
    if (!screen) return undefined;
    const onScreenPress = (event) => {
      if (event.target && event.target.closest && event.target.closest(".sltv-pwa-media-bar, .sltv-pwa-landscape-exit")) return;
      revealPwaMediaBar();
    };
    const media = pwaMediaElement();
    const onPaused = () => {
      if (pwaMediaHideRef.current) clearTimeout(pwaMediaHideRef.current);
      pwaMediaHideRef.current = null;
      setShowPwaMediaBar(true);
      refreshPwaMediaState();
    };
    const onPlaying = () => {
      setShowPwaMediaBar(true);
      refreshPwaMediaState();
      schedulePwaMediaHide();
    };
    screen.addEventListener("pointerup", onScreenPress, { passive: true });
    if (media) {
      media.addEventListener("pause", onPaused);
      media.addEventListener("play", onPlaying);
    }
    const tick = setInterval(refreshPwaMediaState, 500);
    return () => {
      screen.removeEventListener("pointerup", onScreenPress);
      if (media) {
        media.removeEventListener("pause", onPaused);
        media.removeEventListener("play", onPlaying);
      }
      clearInterval(tick);
      if (pwaMediaHideRef.current) clearTimeout(pwaMediaHideRef.current);
    };
  }, [pwaLandscapeView, isFullscreen]);

  const pwaTogglePlayback = () => {
    const media = pwaMediaElement();
    if (!media) return;
    if (media.paused) media.play().catch(() => {}); else media.pause();
    keepPwaMediaBarAlive();
    setTimeout(refreshPwaMediaState, 0);
  };

  const pwaSeekBy = (seconds) => {
    const media = pwaMediaElement();
    if (!media || !Number.isFinite(media.duration)) return;
    media.currentTime = Math.max(0, Math.min(media.duration, (media.currentTime || 0) + seconds));
    keepPwaMediaBarAlive();
    refreshPwaMediaState();
  };

  const pwaSeekTo = (value) => {
    const media = pwaMediaElement();
    if (!media || !Number.isFinite(media.duration)) return;
    media.currentTime = Math.max(0, Math.min(media.duration, Number(value) || 0));
    keepPwaMediaBarAlive();
    refreshPwaMediaState();
  };

  const pwaToggleMute = () => {
    const media = pwaMediaElement();
    if (!media) return;
    media.muted = !media.muted;
    keepPwaMediaBarAlive();
    refreshPwaMediaState();
  };

  const pwaSetVolume = (value) => {
    const media = pwaMediaElement();
    if (!media) return;
    media.volume = Math.max(0, Math.min(1, Number(value)));
    if (media.volume > 0) media.muted = false;
    keepPwaMediaBarAlive();
    refreshPwaMediaState();
  };

  const pwaFormatTime = (seconds) => {
    const total = Math.max(0, Math.floor(Number(seconds) || 0));
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
  };

  useEffect(() => {
    const owned = pwaLandscapeView && showPwaMediaBar;
    document.documentElement.classList.toggle("sltv-pwa-media-controls-visible", owned);
    return () => document.documentElement.classList.remove("sltv-pwa-media-controls-visible");
  }, [pwaLandscapeView, showPwaMediaBar]);

  const dismissPwaLandscapeView = () => {
    pwaLandscapeDismissedRef.current = true;
    setPwaLandscapeView(false);
    setShowPwaLandscapeExit(false);
    setShowPwaFullscreenHint(false);
    if (pwaFullscreenHintTimer.current) clearTimeout(pwaFullscreenHintTimer.current);
    document.documentElement.classList.remove("sltv-pwa-landscape-view");
  };

  const togglePlayerFullscreen = async () => {
    const standalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true;
    const w = Math.max(0, Number(window.innerWidth) || 0);
    const h = Math.max(0, Number(window.innerHeight) || 0);
    const phoneLandscape = w > h && Math.min(w, h) <= 640 && Math.max(w, h) <= 1024;

    // Phase 2.3.91 — user-initiated mobile/PWA fullscreen should attempt the
    // browser/OS Fullscreen API first. The existing scartlead.tv landscape
    // television view remains the safe fallback when standalone Android
    // refuses or does not expose native fullscreen.
    const target = screenRef.current;
    if (!target) return;

    const fullscreenElement =
      document.fullscreenElement || document.webkitFullscreenElement;
    const exit =
      document.exitFullscreen || document.webkitExitFullscreen;
    const request =
      target.requestFullscreen || target.webkitRequestFullscreen;

    try {
      if (fullscreenElement) {
        if (typeof exit === "function") {
          await exit.call(document);
        }
        return;
      }

      if (typeof request === "function") {
        await request.call(target);

        if (document.fullscreenElement || document.webkitFullscreenElement) {
          return;
        }
      }
    } catch (_) {
      // Fall through to the established standalone landscape presentation.
    }

    if (standalone && phoneLandscape) {
      pwaLandscapeDismissedRef.current = false;
      setPwaLandscapeView(true);
      setShowPwaLandscapeExit(true);
      setShowPwaFullscreenHint(true);
      document.documentElement.classList.add("sltv-pwa-landscape-view");
      if (pwaFullscreenHintTimer.current) clearTimeout(pwaFullscreenHintTimer.current);
      pwaFullscreenHintTimer.current = setTimeout(() => setShowPwaFullscreenHint(false), 3200);
    }
  };

  const holdDvrSlate = () => {
    if (bannerOutTimer.current) {
      clearTimeout(bannerOutTimer.current);
      bannerOutTimer.current = null;
    }
    setBannerStage("in");
    setScheduleStage("in");
  };

  const releaseDvrSlate = () => {
    // Phase 2.3.60r1 — pause owns the banner completely. Pointer-leave /
    // focus-leave must never start a hide timer while playback is paused.
    if (isPaused) return;

    if (bannerOutTimer.current) clearTimeout(bannerOutTimer.current);
    bannerOutTimer.current = setTimeout(() => {
      setBannerStage("out");
      setScheduleStage("out");
      setTimeout(() => {
        setBannerStage("hidden");
        setScheduleStage("hidden");
      }, 600);
    }, 7000);
  };

  // Phase 2.3.60 — when playback is paused, NOW BROADCASTING appears
  // immediately and remains pinned until playback resumes. On resume, keep
  // the banner visible for a short read-out window, then slide it away.
  useEffect(() => {
    if (maintenanceSuspendedRef.current || !currentVideo || mode !== "tv" || isStatic) return undefined;

    if (pauseBannerDismissTimer.current) {
      clearTimeout(pauseBannerDismissTimer.current);
      pauseBannerDismissTimer.current = null;
    }

    if (isPaused) {
      pauseBannerHeldRef.current = true;
      showPlaybackFeedback("pause", "PAUSED", progress.current || 0, true);

      if (bannerInTimer.current) {
        clearTimeout(bannerInTimer.current);
        bannerInTimer.current = null;
      }
      if (bannerOutTimer.current) {
        clearTimeout(bannerOutTimer.current);
        bannerOutTimer.current = null;
      }

      setBannerStage("in");
      setScheduleStage("in");
      return undefined;
    }

    // Ignore ordinary PLAYING state unless this banner was specifically
    // pinned by a previous pause.
    if (!pauseBannerHeldRef.current) return undefined;

    pauseBannerHeldRef.current = false;
    showPlaybackFeedback("play", "PLAY", progress.current || 0);

    pauseBannerDismissTimer.current = setTimeout(() => {
      setBannerStage("out");
      setScheduleStage("out");

      pauseBannerDismissTimer.current = setTimeout(() => {
        setBannerStage("hidden");
        setScheduleStage("hidden");
        pauseBannerDismissTimer.current = null;
      }, 600);
    }, 2000);

    return () => {
      if (pauseBannerDismissTimer.current) {
        clearTimeout(pauseBannerDismissTimer.current);
        pauseBannerDismissTimer.current = null;
      }
    };
  }, [isPaused, currentVideo, mode, isStatic]);

  // Phase 2.3.79d-r6.2 — mobile-owned fullscreen bridge.
  // Phase 2.3.91 — powered-off landscape activation.
  // Some mobile browsers will not grant native fullscreen from orientation
  // alone before the viewer has interacted. Use the existing OFF-screen tap
  // as that gesture, then power the television normally.
  const activatePoweredOffScreen = () => {
    const w = Math.max(0, Number(window.innerWidth) || 0);
    const h = Math.max(0, Number(window.innerHeight) || 0);
    const phoneLandscape =
      w > h && Math.min(w, h) <= 640 && Math.max(w, h) <= 1024;

    const fullscreenElement =
      document.fullscreenElement || document.webkitFullscreenElement;

    if (phoneLandscape && !fullscreenElement) {
      const screen = screenRef.current;
      const request =
        screen && (screen.requestFullscreen || screen.webkitRequestFullscreen);

      if (screen && typeof request === "function") {
        try {
          const pending = request.call(screen);
          Promise.resolve(pending)
            .then(() => {
              autoLandscapeFullscreenRef.current =
                !!(document.fullscreenElement || document.webkitFullscreenElement);
            })
            .catch(() => {
              autoLandscapeFullscreenRef.current = false;
            });
        } catch (_) {
          autoLandscapeFullscreenRef.current = false;
        }
      }
    }

    if (onPower) onPower();
  };

  const toggleMobileFullscreen = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const screen = screenRef.current;
    if (!screen) return;

    try {
      const fsElement = document.fullscreenElement || document.webkitFullscreenElement;
      if (fsElement) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (typeof exit === "function") {
          await exit.call(document);
        }
        return;
      }

      const request = screen.requestFullscreen || screen.webkitRequestFullscreen;
      if (typeof request === "function") {
        await request.call(screen);
      }
    } catch (_) {
      // Fullscreen can be denied by the browser. Playback must remain untouched.
    }
  };

  useEffect(() => {
    const onMobileFullscreen = () => {
      const standalone =
        (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
        window.navigator.standalone === true;
      const w = Math.max(0, Number(window.innerWidth) || 0);
      const h = Math.max(0, Number(window.innerHeight) || 0);
      const phoneLandscape = w > h && Math.min(w, h) <= 640 && Math.max(w, h) <= 1024;
      if (standalone && phoneLandscape) {
        pwaLandscapeDismissedRef.current = false;
        setPwaLandscapeView(true);
        setShowPwaLandscapeExit(true);
        setShowPwaFullscreenHint(true);
        document.documentElement.classList.add("sltv-pwa-landscape-view");
        if (pwaFullscreenHintTimer.current) clearTimeout(pwaFullscreenHintTimer.current);
        pwaFullscreenHintTimer.current = setTimeout(() => setShowPwaFullscreenHint(false), 3200);
        return;
      }
      toggleMobileFullscreen();
    };
    window.addEventListener("crt:mobile-fullscreen-toggle", onMobileFullscreen);
    return () => window.removeEventListener("crt:mobile-fullscreen-toggle", onMobileFullscreen);
  }, []);

  // Phase 2.3.79e-r5.2 — browser-safe phone landscape fullscreen.
  // No CSS/pseudo-fullscreen fallback: rotation may request native fullscreen only for an active phone TV session. If the
  // browser rejects the request (for example because user activation is not
  // available), playback is deliberately left untouched. Portrait exits only
  // fullscreen that this orientation handler successfully opened itself.
  useEffect(() => {
    let settleTimer = null;

    const fullscreenElement = () => document.fullscreenElement || document.webkitFullscreenElement;
    const isPhoneLandscape = () => {
      const w = Math.max(0, Number(window.innerWidth) || 0);
      const h = Math.max(0, Number(window.innerHeight) || 0);
      if (!w || !h) return false;
      return w > h && Math.min(w, h) <= 640 && Math.max(w, h) <= 1024;
    };

    const requestPhoneLandscapeFullscreen = async () => {
      // Phase 2.3.91 — phone rotation owns genuine fullscreen whether the
      // television is currently powered on or off. This lets a viewer rotate
      // first, then power/tune the television while already in fullscreen.
      if (!isPhoneLandscape()) return;
      // Phase 2.3.91 — allow the portrait -> landscape orientation bridge to
      // attempt genuine fullscreen in an installed PWA too. Browsers that
      // require a fresh user activation may reject this request; in that case
      // playback remains untouched and the established PWA landscape view
      // continues to provide the fallback presentation.
      if (fullscreenElement()) return;

      const screen = screenRef.current;
      if (!screen) return;
      const request = screen.requestFullscreen || screen.webkitRequestFullscreen;
      if (typeof request !== "function") return;

      try {
        await request.call(screen);
        autoLandscapeFullscreenRef.current = !!fullscreenElement();
      } catch (_) {
        autoLandscapeFullscreenRef.current = false;
      }
    };

    const exitOwnedLandscapeFullscreen = async () => {
      if (!autoLandscapeFullscreenRef.current) return;
      autoLandscapeFullscreenRef.current = false;
      if (!fullscreenElement()) return;

      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (typeof exit !== "function") return;
      try {
        await exit.call(document);
      } catch (_) {
        // Browser owns fullscreen permission/state; playback remains untouched.
      }
    };

    const syncOrientation = () => {
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        const landscape = isPhoneLandscape();
        const previous = lastPhoneLandscapeRef.current;
        lastPhoneLandscapeRef.current = landscape;

        // First observation establishes orientation only. Automatic fullscreen
        // is intentionally tied to an actual portrait -> landscape rotation.
        if (previous === null) return;
        if (!previous && landscape) requestPhoneLandscapeFullscreen();
        if (previous && !landscape) exitOwnedLandscapeFullscreen();
      }, 140);
    };

    // Establish the initial phone orientation without entering fullscreen.
    lastPhoneLandscapeRef.current = isPhoneLandscape();
    window.addEventListener("orientationchange", syncOrientation);
    window.addEventListener("resize", syncOrientation);

    return () => {
      if (settleTimer) clearTimeout(settleTimer);
      window.removeEventListener("orientationchange", syncOrientation);
      window.removeEventListener("resize", syncOrientation);
    };
  }, [power, mode, currentVideo]);

  // Remote → CRT bridge for the new Play/Pause button. App.js owns the
  // Remote, but the player itself lives here, so we expose the action
  // via a window event instead of threading a ref/imperative handle
  // through. Mirrors the existing `crt:welcome-back-show/hide` pattern.
  useEffect(() => {
    const onRemotePlayPause = () => {
      if (maintenanceSuspendedRef.current) return;
      togglePlayPause();
    };
    window.addEventListener("crt:play-pause", onRemotePlayPause);
    return () => window.removeEventListener("crt:play-pause", onRemotePlayPause);
  }, [currentVideo]);

  // MEGA fullscreen bridge.
  //
  // MEGA's own fullscreen button would fullscreen the cross-origin iframe
  // itself, bypassing our top-only crop and bringing the MEGA hover header
  // back. Instead, a tiny transparent hit-target sits over only MEGA's
  // bottom-right fullscreen icon and fullscreens our local wrapper. Because
  // the wrapper owns the iframe crop, the top bar stays hidden in fullscreen
  // while the rest of MEGA's bottom controls remain untouched.
  const toggleMegaFullscreen = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const wrap = megaWrapRef.current;
    if (!wrap) return;

    try {
      if (document.fullscreenElement === wrap) {
        await document.exitFullscreen();
        return;
      }
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
      if (typeof wrap.requestFullscreen === "function") {
        await wrap.requestFullscreen();
      }
    } catch (_) {
      // Browser denied fullscreen or the viewer left fullscreen mid-request.
      // Keep playback untouched; Esc remains the native exit path.
    }
  };

  // Echo paused-state out to App.js so the remote's Play/Pause button
  // can flip its icon between ▶ and ❚❚ without polling. Fired whenever
  // `isPaused` changes, including the implicit changes via native
  // <video> onPlay/onPause handlers below.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("crt:playback-state", { detail: { paused: !!isPaused } }));
  }, [isPaused]);

  // Pointer-based drag handler: scrubs the playhead live, commits on release.
  const handleSeekPointerDown = (e) => {
    if (!progress.duration) return;
    e.preventDefault();
    const bar = e.currentTarget;
    try { bar.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
    seekDragRef.current = true;
    const rect = bar.getBoundingClientRect();
    const { target } = seekTargetFromEvent(e, rect);
    applySeek(target);
    bumpBar();
    showPlaybackFeedback("seek", "SEEK", target, true);

    const onMove = (ev) => {
      if (!seekDragRef.current) return;
      const { pct, target: t } = seekTargetFromEvent(ev, rect);
      setSeekHoverPct(pct);
      applySeek(t);
      setPlaybackFeedback({ kind: "seek", label: "SEEK", seconds: Math.max(0, t) });
    };
    const onUp = (ev) => {
      seekDragRef.current = false;
      setSeekHoverPct(null);
      const { target: finalTarget } = seekTargetFromEvent(ev, rect);
      applySeek(finalTarget);
      showPlaybackFeedback("seek", "SEEK SET", finalTarget);
      try { bar.releasePointerCapture(ev.pointerId); } catch (_) { /* noop */ }
      bar.removeEventListener("pointermove", onMove);
      bar.removeEventListener("pointerup", onUp);
      bar.removeEventListener("pointercancel", onUp);
    };
    bar.addEventListener("pointermove", onMove);
    bar.addEventListener("pointerup", onUp);
    bar.addEventListener("pointercancel", onUp);
  };

  const handleSeek = (e) => {
    // Click-to-seek fallback (kept for keyboard / non-pointer paths).
    if (seekDragRef.current) return;
    if (!progress.duration) return;
    const { target } = seekTargetFromEvent(e);
    applySeek(target);
    bumpBar();
  };

  return (
    <div
      className="relative w-full max-w-4xl"
      // Cabinet scale — viewer's `cabinet_scale` pref (AUTO or manual
      // 0.75-1.75) is exposed as `--cabinet-scale` on the outer layout
      // wrapper in App.js. Multiply the xl-cap of 995px by it so the
      // TV grows/shrinks in step with the remote. Falls back to 1 when
      // the var is missing (safety net for direct/test mounts).
      style={{ maxWidth: `calc(995px * var(--cabinet-scale, 1))` }}
      data-testid="crt-tv"
    >
      {/* TV cabinet */}
      <div
        className="wood-cabinet cabinet-shell relative rounded-[36px] p-6 md:p-8"
        data-testid="cabinet-shell"
      >
        {/* Phase 2.3.61g.2 — video-reactive four-zone tube halo. */}
        {isViewingProfile && tubeGlow && (
          <div
            ref={tubeHaloRef}
            className="absolute left-6 right-6 top-6 md:left-8 md:right-8 md:top-8 aspect-[4/3] rounded-[34px] pointer-events-none z-0"
            data-testid="crt-natural-tube-halo"
            data-video-reactive="fallback"
            data-video-reactive-reason="initialising"
            aria-hidden="true"
            style={{
              "--glow-pulse": 1,
              "--halo-strength": Math.max(0.18,Math.min(1,(Number(tubeGlowIntensity ?? 15)/100))),
              "--halo-color": tubeGlowColor || "var(--phosphor)",
              "--ambilight-top": "72 190 150",
              "--ambilight-right": "72 190 150",
              "--ambilight-bottom": "72 190 150",
              "--ambilight-left": "72 190 150",
              "--ambilight-level": 0.22,
              opacity: "var(--glow-pulse, 1)",
              animation: tubeGlowAnimated && !tubeGlowAudioReactive && !reduceMotion
                ? "crtNaturalTubeHaloBreathe 4.8s ease-in-out infinite" : undefined,
            }}
          >
            {/* Phase 2.3.61g.2-r5 — continuous seam underglow.
                This sits underneath the reactive zones and is intentionally
                faint: its only job is to prevent tiny black gaps between
                neighbouring blurred elements. */}
            <div className="crt-ambilight-seam-underlay" />

            <div className="crt-ambilight-edge crt-ambilight-top" />
            <div className="crt-ambilight-edge crt-ambilight-right" />
            <div className="crt-ambilight-edge crt-ambilight-bottom" />
            <div className="crt-ambilight-edge crt-ambilight-left" />

            {/* Phase 2.3.61g.2-r4 — corner blends.
                Each corner inherits both neighbouring sampled edge colours,
                eliminating the dark gaps without introducing new sampling zones. */}
            <div className="crt-ambilight-corner crt-ambilight-top-left" />
            <div className="crt-ambilight-corner crt-ambilight-top-right" />
            <div className="crt-ambilight-corner crt-ambilight-bottom-right" />
            <div className="crt-ambilight-corner crt-ambilight-bottom-left" />

            <div className="crt-ambilight-fallback-ring" />
          </div>
        )}

        <style>{`
          @keyframes crtNaturalTubeHaloBreathe {
            0%,100% { transform:scale(.998); filter:brightness(.94); }
            50% { transform:scale(1.008); filter:brightness(1.08); }
          }
          .crt-ambilight-seam-underlay {
            position:absolute;
            inset:-7px;
            border-radius:40px;
            pointer-events:none;
            opacity:calc(.055 + (var(--halo-strength,.5) * .075));
            filter:blur(12px);
            mix-blend-mode:screen;
            transition:opacity 650ms ease;
            background:
              linear-gradient(to right,
                rgb(var(--ambilight-left) / .44) 0%,
                rgb(var(--ambilight-top) / .28) 28%,
                rgb(var(--ambilight-top) / .28) 72%,
                rgb(var(--ambilight-right) / .44) 100%) top / 100% 16px no-repeat,
              linear-gradient(to right,
                rgb(var(--ambilight-left) / .42) 0%,
                rgb(var(--ambilight-bottom) / .26) 28%,
                rgb(var(--ambilight-bottom) / .26) 72%,
                rgb(var(--ambilight-right) / .42) 100%) bottom / 100% 16px no-repeat,
              linear-gradient(to bottom,
                rgb(var(--ambilight-top) / .42) 0%,
                rgb(var(--ambilight-left) / .24) 30%,
                rgb(var(--ambilight-left) / .24) 70%,
                rgb(var(--ambilight-bottom) / .42) 100%) left / 16px 100% no-repeat,
              linear-gradient(to bottom,
                rgb(var(--ambilight-top) / .42) 0%,
                rgb(var(--ambilight-right) / .24) 30%,
                rgb(var(--ambilight-right) / .24) 70%,
                rgb(var(--ambilight-bottom) / .42) 100%) right / 16px 100% no-repeat;
            box-shadow:
              inset 0 0 14px 1px rgb(var(--ambilight-top) / .08),
              0 0 18px 2px rgb(var(--ambilight-bottom) / .05);
          }

          .crt-ambilight-edge {
            position:absolute; pointer-events:none; filter:blur(18px);
            opacity:calc(var(--ambilight-level,.34) * (0.34 + (var(--halo-strength,.5) * .66)));
            transition:background-color 520ms ease,opacity 650ms ease;
            mix-blend-mode:screen;
          }
          .crt-ambilight-top { left:4%;right:4%;top:-11px;height:30px;border-radius:999px;
            background:rgb(var(--ambilight-top));box-shadow:0 -14px 42px 10px rgb(var(--ambilight-top)/.70),0 -24px 70px 18px rgb(var(--ambilight-top)/.26); }
          .crt-ambilight-bottom { left:4%;right:4%;bottom:-11px;height:30px;border-radius:999px;
            background:rgb(var(--ambilight-bottom));box-shadow:0 14px 42px 10px rgb(var(--ambilight-bottom)/.66),0 24px 70px 18px rgb(var(--ambilight-bottom)/.24); }
          .crt-ambilight-left { top:4%;bottom:4%;left:-11px;width:30px;border-radius:999px;
            background:rgb(var(--ambilight-left));box-shadow:-14px 0 42px 10px rgb(var(--ambilight-left)/.68),-24px 0 70px 18px rgb(var(--ambilight-left)/.25); }
          .crt-ambilight-right { top:4%;bottom:4%;right:-11px;width:30px;border-radius:999px;
            background:rgb(var(--ambilight-right));box-shadow:14px 0 42px 10px rgb(var(--ambilight-right)/.68),24px 0 70px 18px rgb(var(--ambilight-right)/.25); }

          .crt-ambilight-corner {
            position:absolute;
            width:68px;
            height:68px;
            pointer-events:none;
            border-radius:50%;
            filter:blur(16px);
            opacity:calc(var(--ambilight-level,.34) * (0.32 + (var(--halo-strength,.5) * .68)));
            transition:background 520ms ease,opacity 650ms ease;
            mix-blend-mode:screen;
          }

          .crt-ambilight-top-left {
            left:-25px;
            top:-25px;
            background:
              radial-gradient(circle at 62% 62%,
                rgb(var(--ambilight-top) / .82) 0%,
                rgb(var(--ambilight-left) / .68) 42%,
                transparent 78%);
            box-shadow:
              -12px -12px 38px 8px rgb(var(--ambilight-top) / .26),
              -18px -6px 44px 10px rgb(var(--ambilight-left) / .24);
          }

          .crt-ambilight-top-right {
            right:-25px;
            top:-25px;
            background:
              radial-gradient(circle at 38% 62%,
                rgb(var(--ambilight-top) / .82) 0%,
                rgb(var(--ambilight-right) / .68) 42%,
                transparent 78%);
            box-shadow:
              12px -12px 38px 8px rgb(var(--ambilight-top) / .26),
              18px -6px 44px 10px rgb(var(--ambilight-right) / .24);
          }

          .crt-ambilight-bottom-right {
            right:-25px;
            bottom:-25px;
            background:
              radial-gradient(circle at 38% 38%,
                rgb(var(--ambilight-bottom) / .80) 0%,
                rgb(var(--ambilight-right) / .66) 42%,
                transparent 78%);
            box-shadow:
              12px 12px 38px 8px rgb(var(--ambilight-bottom) / .24),
              18px 6px 44px 10px rgb(var(--ambilight-right) / .24);
          }

          .crt-ambilight-bottom-left {
            left:-25px;
            bottom:-25px;
            background:
              radial-gradient(circle at 62% 38%,
                rgb(var(--ambilight-bottom) / .80) 0%,
                rgb(var(--ambilight-left) / .66) 42%,
                transparent 78%);
            box-shadow:
              -12px 12px 38px 8px rgb(var(--ambilight-bottom) / .24),
              -18px 6px 44px 10px rgb(var(--ambilight-left) / .24);
          }
          .crt-ambilight-fallback-ring { position:absolute;inset:0;border-radius:34px;
            border:2px solid color-mix(in srgb,var(--halo-color) 52%,transparent);
            box-shadow:0 0 16px 3px color-mix(in srgb,var(--halo-color) 38%,transparent),
              0 0 38px 10px color-mix(in srgb,var(--halo-color) 18%,transparent);
            opacity:calc(.28 + (var(--halo-strength,.5) * .58));transition:opacity 500ms ease; }
          [data-video-reactive="active"] .crt-ambilight-fallback-ring { opacity:.04; }
          [data-video-reactive="fallback"] .crt-ambilight-edge,
          [data-video-reactive="fallback"] .crt-ambilight-corner,
          [data-video-reactive="fallback"] .crt-ambilight-seam-underlay { opacity:0; }
        `}</style>

        {/* Screen housing */}
        <div
          ref={screenRef}
          data-phase-glow="2.3.51k"
          className={`crt-screen relative z-10 w-full aspect-[4/3] bg-black rounded-[28px] border-[14px] border-[#0a0a0a] overflow-hidden ${
            isViewingProfile
              ? "shadow-[inset_0_0_40px_rgba(0,0,0,1)]"
              : "shadow-[inset_0_0_12px_rgba(0,0,0,0.58)]"
          }`}
        >
          {pwaLandscapeView && showPwaFullscreenHint && (
            <div className="sltv-pwa-fullscreen-hint" data-testid="pwa-fullscreen-hint" role="status">
              TAP EXIT TO LEAVE FULL SCREEN
            </div>
          )}

          {pwaLandscapeView && (
            <button
              type="button"
              className="sltv-pwa-landscape-exit"
              onClick={dismissPwaLandscapeView}
              aria-label="Exit landscape television view"
              data-testid="pwa-landscape-exit"
              style={{
                opacity: showPwaLandscapeExit ? 1 : 0,
                pointerEvents: showPwaLandscapeExit ? "auto" : "none",
              }}
            >
              <span aria-hidden="true">×</span>
              EXIT
            </button>
          )}

          {(pwaLandscapeView || isFullscreen) && (
            <div
              className={`sltv-pwa-media-bar ${showPwaMediaBar ? "is-visible" : ""}`}
              data-testid="pwa-media-bar"
              onPointerDown={keepPwaMediaBarAlive}
            >
              <button type="button" onClick={() => pwaSeekBy(-10)} aria-label="Rewind 10 seconds">↶<span>10</span></button>
              <button type="button" className="sltv-pwa-media-play" onClick={pwaTogglePlayback} aria-label={pwaMediaState.paused ? "Play" : "Pause"}>
                {pwaMediaState.paused ? "▶" : "Ⅱ"}
              </button>
              <button type="button" onClick={() => pwaSeekBy(10)} aria-label="Forward 10 seconds">↷<span>10</span></button>
              <span className="sltv-pwa-media-time">{pwaFormatTime(pwaMediaState.current)}</span>
              <input
                className="sltv-pwa-media-progress"
                type="range"
                min="0"
                max={Math.max(0, pwaMediaState.duration || 0)}
                step="0.25"
                value={Math.min(pwaMediaState.current || 0, pwaMediaState.duration || 0)}
                onChange={(event) => pwaSeekTo(event.target.value)}
                aria-label="Playback position"
              />
              <span className="sltv-pwa-media-time">{pwaFormatTime(pwaMediaState.duration)}</span>
              <button type="button" onClick={pwaToggleMute} aria-label={pwaMediaState.muted ? "Unmute" : "Mute"}>{pwaMediaState.muted ? "🔇" : "🔊"}</button>
              <input
                className="sltv-pwa-media-volume"
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={pwaMediaState.muted ? 0 : pwaMediaState.volume}
                onChange={(event) => pwaSetVolume(event.target.value)}
                aria-label="Volume"
              />
            </div>
          )}

          {isFullscreen && (
            <div
              className="sltv-native-fullscreen-tap-surface"
              data-testid="native-fullscreen-tap-surface"
              aria-hidden="true"
              onPointerUp={() => {
                if (!power) {
                  if (onPower) onPower();
                  return;
                }
                revealPwaMediaBar();
              }}
            />
          )}

          {/* Inner curved screen */}
          <div
            className={`absolute inset-0 crt-curve ${
              power && isViewingProfile
                ? `${
                    scanlineLevel === 2
                      ? "scanlines-heavy"
                      : scanlineLevel === 1
                      ? "scanlines"
                      : ""
                  } vignette ${
                    currentVideo?.kind === "archive"
                      ? "tape-vhs"
                      : currentVideo?.kind === "youtube"
                      ? "tape-digital"
                      : ""
                  }`
                : ""
            }`}
            data-crt-render-profile={isViewingProfile ? "viewing" : "gui"}
          
            data-picture-mode={pictureMode}>
            {/* Off state — black screen with the owner-editable "off line"
                tip centered. Clicking the screen powers the TV on for the
                most discoverable "I see a TV, I want to watch it" gesture
                (in addition to the pulsing POWER button on the remote). */}
            {!power && !shuttingDown && (
              <div
                className="absolute inset-0 bg-[#0A0F0A] flex items-center justify-center cursor-pointer overflow-hidden"
                onClick={activatePoweredOffScreen}
                data-testid="crt-off"
              >
                {/* Idle-state easter egg — after ~30s on the OFF screen
                    a slow reel of TMDb poster artwork fades in behind
                    the viewer-tips banner. Pure decoration; the OFF
                    click-target keeps working. Reel + timing logic
                    live in IdleReel; no-op when the library has no
                    TMDb-backed tapes yet. */}
                <IdleReel apiBase={apiBase} />
                <div className="absolute left-0 right-0 top-1/2 h-[2px] bg-[#1a1a1a]" />
                <div
                  className="relative z-10 text-center pointer-events-none"
                  style={{ fontFamily: "VT323, monospace" }}
                  data-testid="viewer-tips-off"
                >
                  <div className="text-[var(--phosphor)] phosphor-glow border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] bg-black/60 backdrop-blur-sm px-4 py-3 shadow-[0_0_18px_color-mix(in srgb, var(--phosphor) 25%, transparent)]">
                    <div className="chromatic text-sm md:text-base tracking-[0.35em] opacity-85 mb-1">
                      ◢ {(viewerTips && viewerTips.title) || "VIEWER TIPS"} ◣
                    </div>
                    <div className="text-base md:text-xl tracking-[0.25em] animate-pulse">
                      {(viewerTips && viewerTips.off_line) || "PRESS POWER OR CLICK SCREEN TO TUNE IN"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* On state */}
            {(power || shuttingDown) && (
              <div className="absolute inset-0 bg-[#050805] flicker">
                {warmingUp && (
                  <div
                    className="absolute inset-0 z-40 animate-crt-warmup"
                    data-testid="crt-warmup"
                  />
                )}
                {shuttingDown && (
                  <div
                    className="absolute inset-0 z-[41] animate-crt-shutdown"
                    data-testid="crt-shutdown"
                  />
                )}
                {/* Video layer — the same DOM plane is physically handed to the Guide host. */}
                <div
                  ref={liveMediaHomeRef}
                  className="absolute inset-0 w-full h-full"
                  data-testid="crt-live-media-home"
                >
                  <div
                    ref={liveMediaPlaneRef}
                    className="absolute inset-0 w-full h-full"
                    data-testid="crt-live-media-plane"
                  >
                {currentVideo && !isStatic && (mode === "tv" || mode === "guide") && currentVideo.kind === "youtube" && (
                  <>
                    {/* The YT IFrame Player API instantiates the actual iframe
                        inside this container — see the effect below. We can't
                        use a static <iframe> because we need `seekTo()` to
                        jump to the live broadcast position once duration is
                        known. */}
                    <div
                      ref={ytContainerRef}
                      key={`yt-${currentVideo.id}`}
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      style={{
                        opacity: ytRevealed ? 1 : 0,
                        transition: "opacity 600ms ease-out",
                      }}
                      data-testid="crt-youtube"
                    />
                    {/* Click/hover shield — blocks YouTube's hover UI (top title
                        bar, share button, watch-later) from ever surfacing.
                        Catches the user gesture to allow the iframe to start
                        unmuted autoplay if the browser permits it. */}
                    <div
                      className="absolute inset-0 z-10 cursor-default"
                      onContextMenu={(e) => e.preventDefault()}
                      data-testid="youtube-shield"
                    />
                    {/* YouTube branding masks — only visible while the
                        broadcast slate is on screen. YT only leaks its
                        title bar right around channel-change (paused →
                        playing transition), so we don't need the masks
                        once the slate has slid back down. Tied to
                        bannerStage so timing always stays in lock-step
                        with the NOW BROADCASTING reveal. */}
                    {bannerStage !== "hidden" && (
                      <>
                        <div
                          className="absolute inset-x-0 top-0 h-14 md:h-16 z-20 bg-black pointer-events-none"
                          style={{
                            opacity: bannerStage === "in" ? 1 : 0,
                            transition: "opacity 500ms cubic-bezier(0.22, 1, 0.36, 1)",
                          }}
                          data-testid="yt-mask-top"
                        />
                        <div
                          className="absolute inset-x-0 bottom-0 h-12 md:h-14 z-20 bg-black pointer-events-none"
                          style={{
                            opacity: bannerStage === "in" ? 1 : 0,
                            transition: "opacity 500ms cubic-bezier(0.22, 1, 0.36, 1)",
                          }}
                          data-testid="yt-mask-bottom"
                        />
                      </>
                    )}
                  </>
                )}
                {currentVideo && !isStatic && (mode === "tv" || mode === "guide") && currentVideo.kind === "archive" && (
                  <>
                    {/* archive.org embed — plain iframe with autoplay. Unlike
                        YouTube there's no JS API for seek/duration, so we
                        skip the wall-clock broadcast simulation; each tune-in
                        starts the tape from the beginning. */}
                    <iframe
                      key={`ia-${currentVideo.id}`}
                      src={`https://archive.org/embed/${currentVideo.archive_id}?autoplay=1`}
                      title={currentVideo.title || "Internet Archive tape"}
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      style={{
                        opacity: ytRevealed ? 1 : 0,
                        transition: "opacity 600ms ease-out",
                        border: 0,
                      }}
                      allow="autoplay; fullscreen"
                      allowFullScreen
                      data-testid="crt-archive"
                    />
                    {/* Same click/hover shield as YouTube — keeps embed chrome
                        from being interacted with, so the only controls are
                        the user's CRT remote. */}
                    <div
                      className="absolute inset-0 z-10 cursor-default"
                      onContextMenu={(e) => e.preventDefault()}
                      data-testid="archive-shield"
                    />
                  </>
                )}
                {currentVideo && !isStatic && (mode === "tv" || mode === "guide") && currentVideo.kind === "mega" && (
                  <>
                    {/* MEGA embed — top-chrome crop only.

                        MEGA overlays a hover header (title / logo / share) at
                        the top of its cross-origin iframe. We cannot style that
                        internal DOM directly, so the iframe is extended upward
                        by exactly the header height and clipped at the wrapper's
                        top edge. Crucially the iframe's BOTTOM edge stays fixed
                        to the CRT bottom, so MEGA's playback controls remain
                        visible and fully interactive. */}
                    <div
                      ref={megaWrapRef}
                      className="absolute inset-0 bg-black pointer-events-auto overflow-hidden"
                      data-testid="crt-mega-wrap"
                    >
                      <iframe
                        key={`mega-${currentVideo.id}`}
                        src={currentVideo.mega_url}
                        title={currentVideo.title || "Tape"}
                        style={{
                          position: "absolute",
                          left: 0,
                          top: "-46px",
                          width: "100%",
                          height: "calc(100% + 46px)",
                          opacity: ytRevealed ? 1 : 0,
                          transition: "opacity 600ms ease-out",
                          border: 0,
                          display: "block",
                        }}
                        // Fullscreen permission intentionally belongs to
                        // the local wrapper, not the cross-origin iframe.
                        // This keeps the top-only crop active in fullscreen.
                        allow="autoplay"
                        data-testid="crt-mega"
                      />
                      <button
                        type="button"
                        className="absolute bottom-0 right-0 z-20 h-[50px] w-[56px] border-0 bg-transparent p-0 cursor-pointer"
                        onClick={toggleMegaFullscreen}
                        aria-label={isFullscreen ? "Exit MEGA fullscreen" : "Watch MEGA fullscreen"}
                        title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                        data-testid="crt-mega-fullscreen-hitbox"
                      />
                      {/* NOTE: no click-shield here on purpose.

                          Firefox blocks MEGA's cross-origin autoplay
                          unless the viewer clicks the tape once (a
                          user-gesture the iframe needs to authorise
                          audio + playback). A shield with
                          `pointer-events: auto` would swallow that
                          click and lock Firefox users out of playback
                          entirely. Chrome allowed autoplay so the
                          bug went unnoticed for a while.

                          Trade-off: viewers can now right-click and
                          see MEGA's own context menu (Share / Copy
                          link) — cross-origin security prevents us
                          from intercepting it. That menu shows
                          MEGA's URL, not ours, so it's basically the
                          same as pasting the link elsewhere. Not a
                          meaningful scraping risk. */}
                    </div>
                  </>
                )}
                {currentVideo && !isStatic && (mode === "tv" || mode === "guide") && currentVideo.kind !== "youtube" && currentVideo.kind !== "archive" && currentVideo.kind !== "mega" && (
                  <video
                    ref={videoRef}
                    key={`${currentVideo.id}-${canvasSafePlayback ? "cors" : "direct"}`}
                    // Smart-fit: tapes within ~12% of the CRT's native 4:3
                    // aspect (1.333) get `object-cover` so the broadcast
                    // fills the tube corner-to-corner. Wider tapes (16:9
                    // and beyond) switch to `object-contain` so the full
                    // widescreen frame stays visible, with black "tube
                    // surround" bars top + bottom — same compromise old
                    // VHS-on-CRT decks shipped letterboxed.
                    className={`absolute inset-0 w-full h-full ${
                      videoAspect && videoAspect > 1.5 ? "object-contain bg-black" : "object-cover"
                    }`}
                    // z-index promoted above the z-30 bottom-anchored
                    // decorative overlays (LOBBY pill, whats-new ticker,
                    // broadcast slate, next-up ribbon, watermark) so
                    // Chrome's shadow-DOM native controls — which sit
                    // inside this <video> element and inherit its
                    // stacking level within the .crt-curve 3D-transform
                    // stacking context — render on top of those
                    // siblings instead of being visually clipped by
                    // them. Without this the user has to right-click ▸
                    // "Show all controls" to un-hide the control bar,
                    // and the override resets on every refresh. See
                    // `video[data-testid=\"crt-video\"]::-webkit-media-controls-*`
                    // in index.css for the always-visible opacity rule
                    // that complements this z-order fix.
                    style={{ zIndex: 35 }}
                    crossOrigin={canvasSafePlayback ? "anonymous" : undefined}
                    src={`${apiBase}/videos/${currentVideo.id}/${currentVideo.kind === "mega_s4" ? "playback" : "stream"}`}
                    autoPlay={!maintenanceSuspended}
                    playsInline
                    controls={false}
                    disablePictureInPicture
                    onContextMenu={(e) => e.preventDefault()}
                    onError={() => {
                      if (currentVideo?.kind === "mega_s4" && canvasSafePlayback) {
                        // S4 did not accept the CORS-enabled media request.
                        // Preserve playback by remounting without crossOrigin;
                        // Ambilight will stay on the natural fallback halo.
                        try {
                          window.__sltvAmbilightStatus = {
                            state: "PLAYBACK_CORS_FALLBACK",
                            source: currentVideo?.kind || null,
                            videoId: currentVideo?.id || null,
                          };
                        } catch (_) { /* diagnostics only */ }
                        setCanvasSafePlayback(false);
                      }
                    }}
                    onEnded={(e) => {
                      emitAnalyticsPlaying(false);
                      // Tape finished — clear its saved resume point so the
                      // next playthrough starts from 0.
                      if (currentVideo?.id) clearResumePosition(currentVideo.id);
                      if (onVideoEnded) onVideoEnded(e);
                    }}
                    onPlaying={() => {
                      setVideoStarted(true);
                      setIsPaused(false);
                      emitAnalyticsPlaying(true);
                    }}
                    onPause={() => {
                      setIsPaused(true);
                      emitAnalyticsPlaying(false);
                    }}
                    onPlay={(e) => {
                      // Phase 2.3.68-r2.3 — hard maintenance lifecycle barrier.
                      // Even browser-driven autoplay may not survive this event.
                      if (maintenanceSuspendedRef.current) {
                        try { e.currentTarget.pause(); } catch (_) {}
                        setIsPaused(true);
                        return;
                      }
                      // Final lifecycle guard: normal playback becomes audible
                      // only at the current SCARTLEAD volume.
                      e.currentTarget.volume = Math.max(0, Math.min(1, volume / 10));
                      setIsPaused(false);
                    }}
                    onTimeUpdate={(e) => {
                      const t = e.currentTarget.currentTime;
                      const d = e.currentTarget.duration || 0;
                      if (!seekDragRef.current) {
                        setProgress({ current: t, duration: d });
                      }
                      // Throttle persistence to once every 5s of wall-clock.
                      const now = Date.now();
                      if (now - lastSaveRef.current >= 5000 && currentVideo?.id) {
                        lastSaveRef.current = now;
                        saveResumePosition(currentVideo.id, t, d);
                      }
                    }}
                    onLoadedMetadata={(e) => {
                      const el = e.currentTarget;
                      // Apply canonical volume before autoplay can become audibly
                      // established; metadata is available before normal playback.
                      el.volume = Math.max(0, Math.min(1, volume / 10));
                      const d = el.duration || 0;
                      // Cache the intrinsic aspect ratio so the smart-fit
                      // class can switch between cover/contain based on
                      // the tape, not the cabinet.
                      const w = el.videoWidth || 0;
                      const h = el.videoHeight || 0;
                      if (w > 0 && h > 0) setVideoAspect(w / h);
                      // Two competing resume sources, in priority order:
                      // 1. `initialSeekSec` — App.js wants us to seek to the
                      //    broadcast-time position (channel kept playing
                      //    while user was on another channel).
                      // 2. `getResumePosition` — per-tape "continue where
                      //    you left off" persisted across sessions.
                      // Channel-resume wins when set; otherwise we honour
                      // the per-tape resume so users don't lose their place.
                      const broadcastSeek = Math.max(0, Math.floor(initialSeekSec || 0));
                      const liveSeekInBounds =
                        !!liveTuneActive && d > 0 && broadcastSeek >= 0 && broadcastSeek < d;
                      const targetSeek = liveSeekInBounds
                        ? broadcastSeek
                        : (currentVideo?.id ? getResumePosition(currentVideo.id, d) : 0);
                      if (liveSeekInBounds || targetSeek > 0) {
                        try { el.currentTime = targetSeek; } catch (_) { /* some browsers refuse pre-seek */ }
                        setProgress({ current: targetSeek, duration: d });
                        // Surface the resume in the UI for ~2.5s (skip the
                        // "RESUMED FROM" badge for pure broadcast-seeks
                        // since that's expected continuity, not a feature).
                        if (!liveSeekInBounds) {
                          resumedAtRef.current = targetSeek;
                          setResumedAt(targetSeek);
                          if (resumeOverlayTimer.current) clearTimeout(resumeOverlayTimer.current);
                          // 4s to match the `animate-fade-in-out-static`
                          // keyframe duration — gives the viewer time to
                          // actually read "RESUMED FROM 0:15" before it
                          // fades out.
                          resumeOverlayTimer.current = setTimeout(() => setResumedAt(0), 4000);
                        }
                      } else {
                        setProgress({ current: 0, duration: d });
                      }
                    }}
                    onMouseMove={bumpBar}
                    data-testid="crt-video"
                  />
                )}
                  </div>
                </div>

                {/* (SMPTE color-bars pre-roll AND PBS-style test card
                    removed — owner asked to drop both so every tape boot
                    jumps straight from channel-change static to the
                    SHOW STARTING SOON countdown.) */}

                {/* Seek bar overlay — YouTube-only.

                    Native `<video>` tapes render the browser's built-in
                    controls (play/pause/scrub/volume/PiP/fullscreen) via
                    the `controls` attribute set on the element itself,
                    so a second custom overlay on top would just fight
                    them for the same pixels — in Chrome it silently
                    clobbered the native shadow-DOM controls and users
                    had to right-click ▸ "Show all controls" to un-hide
                    them (and even that reset on refresh). So this
                    custom retro bar is now scoped exclusively to
                    YouTube tapes, where our click/hover shield hides
                    YT's own player chrome and the overlay is the only
                    scrub path. MEGA has its own bottom control bar
                    exposed inside its iframe; Archive tapes can't be
                    scrubbed via JS API and were always excluded. */}
                {currentVideo && !isStatic && mode === "tv" && currentVideo.kind === "youtube" && progress.duration > 0 && (
                  <div
                    className={`absolute bottom-0 left-0 right-0 z-30 px-3 pb-2 pt-6 transition-opacity duration-300 ${showBar ? "opacity-100" : "opacity-0 hover:opacity-100"}`}
                    style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85), transparent)" }}
                    onMouseEnter={bumpBar}
                    onMouseMove={bumpBar}
                    onDoubleClick={(e) => e.stopPropagation()}
                    data-testid="seek-overlay"
                  >
                    <div
                      onClick={handleSeek}
                      onPointerDown={handleSeekPointerDown}
                      onPointerMove={(e) => {
                        if (seekDragRef.current) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        setSeekHoverPct(Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)));
                      }}
                      onPointerLeave={() => { if (!seekDragRef.current) setSeekHoverPct(null); }}
                      className="relative h-2 bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)] cursor-pointer group touch-none"
                      data-testid="seek-bar"
                    >
                      {/* Filled progress (current position) */}
                      <div
                        className="absolute inset-y-0 left-0 bg-[var(--phosphor)] shadow-[0_0_8px_var(--phosphor)] pointer-events-none"
                        style={{ width: `${Math.min(100, (progress.current / progress.duration) * 100)}%` }}
                      />
                      {/* Hover preview marker — dim phosphor stripe under the cursor */}
                      {seekHoverPct !== null && !seekDragRef.current && (
                        <div
                          className="absolute top-0 bottom-0 w-px bg-[var(--phosphor)] opacity-50 pointer-events-none"
                          style={{ left: `${seekHoverPct * 100}%` }}
                          data-testid="seek-hover-marker"
                        />
                      )}
                      {/* Scrubber thumb — a small phosphor disc on the
                          playhead. Stays small at rest, grows on hover/drag
                          so the affordance is obvious. */}
                      <div
                        className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full bg-[var(--phosphor)] shadow-[0_0_8px_var(--phosphor)] pointer-events-none transition-transform ${seekDragRef.current ? "scale-150" : "group-hover:scale-125"}`}
                        style={{
                          left: `${Math.min(100, (progress.current / progress.duration) * 100)}%`,
                          width: "12px",
                          height: "12px",
                        }}
                        data-testid="seek-thumb"
                      />
                      {/* Hover-time tooltip — shows the time the cursor is
                          aimed at (not the playhead) so the viewer can pick
                          a spot before committing the seek. */}
                      {seekHoverPct !== null && (
                        <div
                          className="absolute -top-6 -translate-x-1/2 px-1.5 py-0.5 bg-black/90 border border-[var(--phosphor)] text-[10px] text-[var(--phosphor)] tracking-widest pointer-events-none whitespace-nowrap tabular-nums"
                          style={{ left: `${seekHoverPct * 100}%`, fontFamily: "VT323, monospace" }}
                          data-testid="seek-hover-time"
                        >
                          {formatTime(seekHoverPct * progress.duration)}
                        </div>
                      )}
                    </div>
                    <div className="flex justify-between items-center text-[10px] text-[var(--phosphor)] mt-1" style={{ fontFamily: "VT323, monospace" }}>
                      <div className="flex items-center gap-2">
                        {currentVideo.kind !== "archive" && currentVideo.kind !== "mega" && (
                          <button
                            type="button"
                            onClick={togglePlayPause}
                            data-testid="seek-play-pause"
                            aria-label={isPaused ? "Play" : "Pause"}
                            title={isPaused ? "Play" : "Pause"}
                            className="w-6 h-6 -my-1 flex items-center justify-center border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] hover:border-[var(--phosphor)] hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)] transition-colors"
                          >
                            {isPaused ? <Play size={11} /> : <Pause size={11} />}
                          </button>
                        )}
                        <span className="tabular-nums">{formatTime(progress.current)}</span>
                        {currentVideo.kind === "mega" && (
                          <span
                            className="ml-1 text-[var(--phosphor)] tracking-widest"
                            data-testid="mega-click-to-play-hint"
                            title="MEGA requires a direct click inside the video before playback can begin."
                          >
                            ▶ CLICK SCREEN TO PLAY
                          </span>
                        )}
                      </div>
                      <span className="tabular-nums">{formatTime(progress.duration)}</span>
                    </div>
                  </div>
                )}

                {/* Phase 2.3.64.7 — premium playback / pause / seek feedback. */}
                {power && currentVideo && !isStatic && mode === "tv" && playbackFeedback && (
                  <div
                    className="crt-playback-feedback z-[57]"
                    data-testid="playback-feedback"
                    data-phase-playback-feedback="2.3.64.7"
                    data-feedback-kind={playbackFeedback.kind}
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    <div className="crt-playback-feedback__icon" aria-hidden="true">
                      {playbackFeedback.kind === "pause" ? "❚❚" :
                       playbackFeedback.kind === "play" ? "▶" :
                       playbackFeedback.kind === "rewind" ? "↶" :
                       playbackFeedback.kind === "forward" ? "↷" : "◈"}
                    </div>
                    <div className="crt-playback-feedback__label">{playbackFeedback.label}</div>
                    {playbackFeedback.seconds !== null && (
                      <div className="crt-playback-feedback__time">
                        {formatTime(playbackFeedback.seconds)}
                        {progress.duration > 0 ? ` / ${formatTime(progress.duration)}` : ""}
                      </div>
                    )}
                  </div>
                )}

                {/* Phase 2.3.58 — clean channel-only tuner lock.
                    Triggered by currentChannel.id changes, never by a tape
                    advancing inside the same channel. */}
                {power && mode === "tv" && channelTuneActive && (
                  <div
                    className="absolute inset-0 z-[58] pointer-events-none overflow-hidden crt-tune-lock"
                    data-testid="channel-tune-transition"
                    aria-hidden="true"
                  >
                    <div className="crt-tune-lock__shade" />
                    <div className="crt-tune-lock__sync" />
                    <div className="crt-tune-lock__line" />
                  </div>
                )}

                {/* "NEXT UP" countdown ribbon — appears when the current
                    tape is within 30s of ending. Bottom-left so it doesn't
                    overlap the schedule strip (bottom-right) or the seek
                    bar (full-width bottom). Skipped on single-tape channels
                    and on archive.org tapes (we can't see their playhead). */}
                {currentVideo && !isStatic && mode === "tv" &&
                  currentVideo.kind !== "archive" &&
                  Array.isArray(playlist) && playlist.length > 1 &&
                  progress.duration > 0 &&
                  (progress.duration - progress.current) <= 30 &&
                  (progress.duration - progress.current) > 0 && (
                    <div
                      className="absolute bottom-16 left-0 z-[40] pointer-events-none"
                      data-testid="next-up-ribbon"
                    >
                      <div
                        className="bg-black/85 border-y-2 border-r-2 border-[#FFCC00] pl-4 pr-5 py-2 text-[#FFCC00] shadow-[0_0_15px_rgba(255,204,0,0.35)] max-w-[20em]"
                        style={{ fontFamily: "JetBrains Mono, monospace" }}
                      >
                        <div className="text-[10px] tracking-[0.3em] opacity-80 mb-0.5">
                          NEXT UP IN {formatTime(Math.ceil(progress.duration - progress.current))}
                        </div>
                        <div className="text-sm md:text-base truncate font-medium">
                          ▶ {playlist[(videoIndex + 1) % playlist.length]?.title || "(untitled)"}
                        </div>
                      </div>
                    </div>
                  )}

                {/* "NOW / NEXT / LATER" mini schedule strip — bottom-right
                    slate showing where you are in the playlist. Slides in
                    ~2.2s into a channel change (lining up with the end of the
                    NOW BROADCASTING banner) and holds for ~6s. Skipped on
                    single-tape playlists since NEXT/LATER would just repeat. */}
                {/* Combined broadcast slate — single bottom-anchored card
                    that fuses the NOW BROADCASTING ident (left half) with
                    the COMING UP queue (right half). Slides up from below
                    the bezel on every channel change / SELECT press,
                    holds for ~7s, then slides back down. Anchored bottom-
                    center with a 90% max-width so it doesn't crowd the
                    chat pill in the corner. Right-hand queue is hidden
                    for single-tape playlists since NEXT/LATER would just
                    repeat. */}
                {!maintenanceSuspended && currentVideo && !isStatic && mode === "tv" && bannerStage !== "hidden" && screenRef.current && createPortal((
                  <div
                    className="absolute inset-x-0 bottom-3 z-[40] pointer-events-none flex justify-center px-3"
                    style={{
                      transform: bannerStage === "in" ? "translateY(0)" : "translateY(120%)",
                      transition: "transform 500ms cubic-bezier(0.22, 1, 0.36, 1)",
                    }}
                    data-testid="broadcast-slate"
                    data-crt-ui-surface="clean"
                  >
                    <div
                      className="pointer-events-auto border border-[color-mix(in_srgb,var(--phosphor)_78%,transparent)] text-[var(--phosphor)] shadow-[0_12px_46px_rgba(0,0,0,0.72),0_0_18px_color-mix(in_srgb,var(--phosphor)_18%,transparent)] flex w-full max-w-[68em] overflow-hidden backdrop-blur-[1.5px]" data-testid="nb-programme-shell"
                      style={{
                        fontFamily: "JetBrains Mono, monospace",
                        backgroundColor: "rgba(0, 0, 0, 0.985)",
                      }}
                      data-phase="2.3.51g-r2"
                      onPointerEnter={holdDvrSlate}
                      onPointerLeave={releaseDvrSlate}
                      onFocusCapture={holdDvrSlate}
                      onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) releaseDvrSlate(); }}
                      data-testid="dvr-broadcast-controls"
                    >
                      {/* Consolidated programme information — channel, tape, resume state and DVR. */}
                      <div
                        className="flex-1 min-w-0 px-4 py-2 bg-[rgba(0,0,0,0.995)]"
                        data-testid="now-playing-banner"
                        data-phase-programme="2.3.51i"
                      >
                        <div
                          className="flex items-center gap-2 text-[10px] tracking-[0.22em] opacity-80 mb-0.5 min-w-0"
                          data-testid="nb-info-header"
                        >
                          <span className="tracking-[0.35em] shrink-0">┌─ NOW BROADCASTING ─┐</span>
                          <span
                            className="px-1.5 py-0.5 border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] bg-[color-mix(in_srgb,var(--phosphor)_7%,transparent)] font-bold tracking-[0.18em] shrink-0"
                            data-testid="banner-channel-number"
                          >
                            CH {channelNumberLabel || String(channelNumber).padStart(2, "0")}
                          </span>
                          {resumedAt > 0 && (
                            <span
                              className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] opacity-80 tracking-[0.14em] shrink-0"
                              data-testid="banner-resumed"
                              title={`Resumed from ${formatTime(resumedAt)}`}
                            >
                              ↻ RESUMED · {formatTime(resumedAt)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-baseline gap-3">
                          <span className="text-2xl md:text-3xl leading-none chromatic tabular-nums">
                            CH {channelNumberLabel || String(channelNumber).padStart(2, "0")}
                          </span>
                          <span className="text-base md:text-lg leading-none truncate flex-1">
                            {channelName}
                          </span>
                          {channelIsSubChannel && (
                            <span
                              className="text-[9px] tracking-[0.3em] px-1.5 py-0.5 border border-[var(--phosphor)] text-[var(--phosphor)] shrink-0"
                              data-testid="sub-channel-badge-banner"
                              title={channelParentName ? `Sub-channel of ${channelParentName}` : "Sub-channel"}
                            >
                              SUB
                            </span>
                          )}
                          <span className="text-[10px] opacity-60 tabular-nums tracking-widest ml-auto pl-2 shrink-0">
                            {bannerClock}
                          </span>
                        </div>
                        {channelIsSubChannel && channelParentName && (
                          <div className="text-[10px] opacity-60 tracking-widest mt-0.5 truncate" data-testid="sub-channel-parent-hint">
                            ↳ SUB-CHANNEL OF {(channelParentName || "").toUpperCase()}
                          </div>
                        )}
                        {currentVideo.title && (
                          <div className="mt-2 text-base md:text-lg opacity-95 truncate leading-snug" data-testid="banner-title">
                            ▶ {stripMediaExt(currentVideo.title)}
                          </div>
                        )}
                        {canControlTransport && progress.duration > 0 && (
                          <div className="mt-3 pt-3 border-t border-[color-mix(in_srgb,var(--phosphor)_24%,transparent)]" data-testid="banner-dvr-panel">
                            <div className="flex items-center gap-2 text-[10px] tracking-[0.16em] opacity-95 mb-2">
                              <span className="inline-flex items-center gap-1.5">
                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${isPaused ? "bg-[#FFCC00]" : "bg-[#FF3B30] animate-pulse"} shadow-[0_0_6px_currentColor]`} />
                                <span className={isPaused ? "text-[#FFCC00] font-bold" : "text-[#FF3B30] font-bold"}>
                                  {isPaused ? "PAUSED" : "PLAYING"}
                                </span>
                              </span>
                              <span
                                className="px-1.5 py-0.5 border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] bg-[color-mix(in_srgb,var(--phosphor)_8%,transparent)] text-[8px] font-bold tracking-[0.2em]"
                                data-testid="banner-dvr-mode-badge"
                              >
                                DVR
                              </span>
                              <span className="opacity-50">·</span>
                              <span className="tabular-nums">
                                {formatTime(progress.current)} / {formatTime(progress.duration)}
                              </span>
                              <span className="opacity-50 ml-auto">
                                −{formatTime(Math.max(0, progress.duration - progress.current))}
                              </span>
                            </div>

                            <div
                              className="relative h-5 group cursor-pointer touch-none mb-2"
                              onPointerDown={handleSeekPointerDown}
                              onClick={handleSeek}
                              data-testid="banner-dvr-seek"
                              role="slider"
                              aria-label="Playback position"
                              aria-valuemin={0}
                              aria-valuemax={Math.round(progress.duration)}
                              aria-valuenow={Math.round(progress.current)}
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "ArrowLeft") { e.preventDefault(); dvrSeekRelative(-10); }
                                if (e.key === "ArrowRight") { e.preventDefault(); dvrSeekRelative(30); }
                              }}
                            >
                              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1.5 bg-[color-mix(in_srgb,var(--phosphor)_18%,transparent)] border border-[color-mix(in_srgb,var(--phosphor)_16%,transparent)]">
                                <div
                                  className="h-full bg-[var(--phosphor)] shadow-[0_0_10px_color-mix(in_srgb,var(--phosphor)_70%,transparent)]"
                                  style={{ width: `${Math.max(0, Math.min(100, (progress.current / progress.duration) * 100))}%` }}
                                />
                              </div>
                              <div
                                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[var(--phosphor)] border border-black/70 shadow-[0_0_12px_color-mix(in_srgb,var(--phosphor)_85%,transparent)]"
                                style={{ left: `${Math.max(0, Math.min(100, (progress.current / progress.duration) * 100))}%` }}
                              />
                            </div>

                            <div className="grid grid-cols-[auto_auto_auto] items-center gap-3 md:gap-4" data-testid="banner-dvr-buttons">
                              <div className="flex items-center gap-1.5" data-testid="nb-transport-group">
                                <span className="hidden lg:inline text-[8px] tracking-[0.18em] opacity-45 mr-1">PLAYBACK</span>
                              {Array.isArray(playlist) && playlist.length > 1 && (
                                <button type="button" onClick={() => dvrStepTape(-1)} className="inline-flex items-center justify-center gap-0.5 h-8 min-w-8 px-2 border border-[color-mix(in_srgb,var(--phosphor)_58%,transparent)] bg-black/35 hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)] focus:outline-none focus:ring-1 focus:ring-[var(--phosphor)] transition-colors" title="Previous tape / episode" aria-label="Previous tape or episode">
                                  <SkipBack size={13} />
                                </button>
                              )}
                              <button type="button" onClick={() => dvrSeekRelative(-10)} className="inline-flex items-center justify-center gap-0.5 h-8 min-w-8 px-2 border border-[color-mix(in_srgb,var(--phosphor)_58%,transparent)] bg-black/35 hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)] focus:outline-none focus:ring-1 focus:ring-[var(--phosphor)] transition-colors" title="Rewind 10 seconds" aria-label="Rewind 10 seconds">
                                <RotateCcw size={13} /><span className="text-[9px]">10</span>
                              </button>
                              <button type="button" onClick={togglePlayPause} className="inline-flex items-center justify-center gap-0.5 h-8 min-w-8 px-2 border border-[color-mix(in_srgb,var(--phosphor)_58%,transparent)] bg-black/35 hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)] focus:outline-none focus:ring-1 focus:ring-[var(--phosphor)] transition-colors bg-[color-mix(in_srgb,var(--phosphor)_12%,transparent)]" title={isPaused ? "Play" : "Pause"} aria-label={isPaused ? "Play" : "Pause"}>
                                {isPaused ? <Play size={14} /> : <Pause size={14} />}
                              </button>
                              <button type="button" onClick={() => dvrSeekRelative(30)} className="inline-flex items-center justify-center gap-0.5 h-8 min-w-8 px-2 border border-[color-mix(in_srgb,var(--phosphor)_58%,transparent)] bg-black/35 hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)] focus:outline-none focus:ring-1 focus:ring-[var(--phosphor)] transition-colors" title="Fast forward 30 seconds" aria-label="Fast forward 30 seconds">
                                <RotateCw size={13} /><span className="text-[9px]">30</span>
                              </button>
                              {Array.isArray(playlist) && playlist.length > 1 && (
                                <button type="button" onClick={() => dvrStepTape(1)} className="inline-flex items-center justify-center gap-0.5 h-8 min-w-8 px-2 border border-[color-mix(in_srgb,var(--phosphor)_58%,transparent)] bg-black/35 hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)] focus:outline-none focus:ring-1 focus:ring-[var(--phosphor)] transition-colors" title="Next tape / episode" aria-label="Next tape or episode">
                                  <SkipForward size={13} />
                                </button>
                              )}
                              </div>
                              <div className="flex items-center gap-1.5" data-testid="nb-audio-group">
                                <span className="hidden lg:inline text-[8px] tracking-[0.18em] opacity-45 mr-1">AUDIO</span>
                              <button type="button" onClick={dvrToggleMute} className="inline-flex items-center justify-center gap-0.5 h-8 min-w-8 px-2 border border-[color-mix(in_srgb,var(--phosphor)_58%,transparent)] bg-black/35 hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)] focus:outline-none focus:ring-1 focus:ring-[var(--phosphor)] transition-colors" title={!dvrMuted ? "Mute" : "Unmute"} aria-label={!dvrMuted ? "Mute" : "Unmute"}>
                                {!dvrMuted ? <Volume2 size={13} /> : <VolumeX size={13} />}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  dvrAdjustVolume(-1);
                                }}
                                className="inline-flex items-center justify-center h-8 min-w-8 px-2 border border-[color-mix(in_srgb,var(--phosphor)_58%,transparent)] bg-black/35 hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)] focus:outline-none focus:ring-1 focus:ring-[var(--phosphor)] transition-colors"
                                title="Volume down"
                                aria-label="Volume down"
                                data-testid="banner-dvr-volume-down"
                              >
                                <span className="text-[12px] font-bold">−</span>
                              </button>
                              <span
                                className="min-w-10 text-center text-[10px] tabular-nums tracking-widest opacity-85"
                                data-testid="banner-dvr-volume"
                                aria-label={`Volume ${dvrMuted ? 0 : dvrVolume} of 10`}
                              >
                                VOL {dvrMuted ? 0 : dvrVolume}/10
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  dvrAdjustVolume(1);
                                }}
                                className="inline-flex items-center justify-center h-8 min-w-8 px-2 border border-[color-mix(in_srgb,var(--phosphor)_58%,transparent)] bg-black/35 hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)] focus:outline-none focus:ring-1 focus:ring-[var(--phosphor)] transition-colors"
                                title="Volume up"
                                aria-label="Volume up"
                                data-testid="banner-dvr-volume-up"
                              >
                                <span className="text-[12px] font-bold">+</span>
                              </button>
                              </div>
                              <div className="flex items-center gap-1.5" data-testid="nb-view-group">
                                <span className="hidden lg:inline text-[8px] tracking-[0.18em] opacity-45 mr-1">VIEW</span>
                              <button type="button" onClick={togglePlayerFullscreen} className="inline-flex items-center justify-center gap-0.5 h-8 min-w-8 px-2 border border-[color-mix(in_srgb,var(--phosphor)_58%,transparent)] bg-black/35 hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)] focus:outline-none focus:ring-1 focus:ring-[var(--phosphor)] transition-colors" title={isFullscreen ? "Exit fullscreen" : "Fullscreen"} aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                                {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
                              </button>
                              </div>
                            </div>
                          </div>
                        )}
                        {!canControlTransport && (
                          <div className="mt-1 text-[10px] tracking-[0.18em] opacity-60" data-testid="banner-dvr-unavailable">
                            EMBED CONTROLS AVAILABLE ON VIDEO
                          </div>
                        )}
                        {/* Synopsis — prefers the current tape's own
                            description ("episode synopsis") when set,
                            falling back to the channel description as
                            the ambient "about this channel" copy. Owner
                            asked for the per-tape synopsis to surface
                            here instead of cluttering every row in the
                            EPISODES list. Clamped to 2 lines so the
                            banner never grows tall enough to hide the
                            playing video. */}
                        {(currentVideo.description || channelDescription) && (
                          <div
                            className="mt-3 -mx-4 px-4 pt-3 pb-2 border-t border-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] bg-[rgb(0,0,0)]"
                            data-testid="banner-description"
                            data-phase-synopsis="2.3.51h"
                          >
                            <div className="flex items-center justify-between gap-3 mb-1.5">
                              <span className="text-[9px] tracking-[0.22em] font-bold opacity-100">ABOUT THIS EPISODE</span>
                              {(currentVideo.description || channelDescription).length > 190 && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setSynopsisExpanded((v) => !v); holdDvrSlate(); }}
                                  className="text-[9px] tracking-[0.16em] px-2 py-1 border border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] bg-[rgba(0,0,0,0.96)] hover:bg-[color-mix(in_srgb,var(--phosphor)_12%,black)] focus:outline-none focus:ring-1 focus:ring-[var(--phosphor)]"
                                  data-testid="nb-synopsis-toggle"
                                  aria-expanded={synopsisExpanded}
                                >
                                  {synopsisExpanded ? "LESS <" : "MORE >"}
                                </button>
                              )}
                            </div>
                            <div className={`text-[11px] md:text-[12px] leading-[1.6] opacity-100 ${synopsisExpanded ? "" : "line-clamp-3"}`} title={currentVideo.description || channelDescription}>
                              {currentVideo.description || channelDescription}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* RIGHT — COMING UP queue. Only meaningful when the
                          channel has at least 2 tapes. */}
                      {Array.isArray(playlist) && playlist.length > 1 && (
                        <div
                          className="hidden"
                          data-testid="schedule-strip"
                        >
                          <div className="text-[10px] tracking-[0.35em] opacity-70 mb-1">
                            ┌─ COMING UP ─┐
                          </div>
                          {[
                            { label: "NEXT",  offset: 1, testid: "schedule-next" },
                            { label: "LATER", offset: 2, testid: "schedule-later" },
                          ].map(({ label, offset, testid }) => {
                            const idx = (videoIndex + offset) % playlist.length;
                            const item = playlist[idx];
                            if (!item) return null;
                            return (
                              <div
                                key={label}
                                className={`flex items-baseline gap-2 leading-tight opacity-75 ${offset > 1 ? "mt-1" : ""}`}
                                data-testid={testid}
                              >
                                <span className="text-[10px] tracking-[0.25em] shrink-0 w-12">
                                  {label}
                                </span>
                                <span className="text-xs truncate">
                                  {stripMediaExt(item.title) || "(untitled)"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ), screenRef.current)}

                {/* "Resumed from m:ss" overlay — flashes briefly when we
                    jump-seek to a previously-watched position. Uses the
                    *-static fade variant because we're anchored to the
                    top-left corner; the regular fade-in-out animation
                    bakes in a translate(-50%, -50%) which would yank
                    this badge half-off-screen behind the bezel. */}
                {false && resumedAt > 0 && currentVideo && !isStatic && mode === "tv" && videoStarted && currentVideo.kind !== "youtube" && (
                  <div
                    className="absolute top-6 left-6 z-50 pointer-events-none text-[var(--phosphor)] phosphor-glow border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] bg-black/65 px-3 py-1.5 animate-fade-in-out-static"
                    style={{ fontFamily: "VT323, monospace" }}
                    data-testid="resumed-overlay"
                  >
                    <div className="text-[10px] tracking-[0.3em] opacity-80">RESUMED FROM</div>
                    <div className="text-2xl leading-none tabular-nums">
                      ▶ {formatTime(resumedAt)}
                    </div>
                  </div>
                )}

                {/* No content placeholder */}
                {!currentVideo && !isStatic && mode === "tv" && lineupReady && (
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center text-[var(--phosphor)] phosphor-glow"
                    style={{ fontFamily: "VT323, monospace" }}
                    data-testid="crt-no-signal"
                  >
                    <div className="text-7xl tracking-widest chromatic">NO SIGNAL</div>
                    {isOwner && (
                      <div className="text-2xl mt-4 opacity-80">PRESS MENU TO UPLOAD</div>
                    )}
                    {!isOwner && (
                      <>
                        <div className="text-2xl mt-4 opacity-80">— STAND BY —</div>
                        <div className="text-lg mt-3 opacity-70 tracking-wider">
                          BEST VIEWED ON PC / LAPTOP
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Phase 2.3.56r2b — programme-picture graphics plane.
                    Follows picture-mode geometry while CRT/player OSD remains fixed. */}
                <div
                  className="absolute inset-0 z-[40] pointer-events-none"
                  style={{
                    transform: broadcastOverlayTransform,
                    transformOrigin: "center center",
                  }}
                  data-testid="broadcast-picture-overlay-plane"
                >
                {/* Channel logo watermark — the "channel bug" that sits
                    in the top-left corner during playback. Suppressed
                    when the PATRON perk `hideDistractions` is on so
                    tippers can enjoy a picture-clean tube. */}
                {currentChannel && (currentChannel.logo_path || currentChannel.logo_url) && !isStatic && mode === "tv" && !hideDistractions && (
                  <img
                    src={
                      // Prefer the raw external URL when it's on a
                      // browser-cacheable CDN — dodges the ingress
                      // `no-store` rewrite so the browser can actually
                      // cache the logo across channel changes. Local
                      // uploads still route through the proxy.
                      (!currentChannel.logo_path && currentChannel.logo_url && isBrowserCacheableHost(currentChannel.logo_url))
                        ? currentChannel.logo_url
                        : `${apiBase}/channels/${currentChannel.id}/logo?v=${currentChannel.logo_path || currentChannel.logo_url}`
                    }
                    alt=""
                    className="absolute top-5 left-6 h-14 w-auto opacity-80 pointer-events-none drop-shadow-[0_0_8px_rgba(0,0,0,0.8)]"
                    data-testid="channel-logo-watermark"
                  />
                )}

                </div>
                {/* Static noise overlay (during channel change) */}
                {isStatic && (
                  <div
                    className="absolute inset-0 static-noise"
                    data-testid="crt-static"
                  />
                )}

                {/* Phase 2.3.56r2b — programme-picture graphics plane.
                    Follows picture-mode geometry while CRT/player OSD remains fixed. */}
                <div
                  className="absolute inset-0 z-[40] pointer-events-none"
                  style={{
                    transform: broadcastOverlayTransform,
                    transformOrigin: "center center",
                  }}
                  data-testid="broadcast-picture-overlay-plane"
                >
                {/* Live "broadcast bug" overlay — owner-configurable text
                    strip rendered OVER the playing video (no FFmpeg burn-in
                    required). Hidden during static + slates + power-off so
                    it never competes with channel changes. Also suppressed
                    when the PATRON perk `hideDistractions` is on — the
                    whole point of that toggle is a picture-clean tube, and
                    the live-overlay is the biggest cosmetic distraction of
                    the lot (defaults to bottom-right SCARTLEAD.TV). Three
                    styles (minimal/boxed/shadow) × four corners × opacity
                    slider drive a small CSS matrix. */}
                {overlay && overlay.enabled && overlay.text && power && mode === "tv" && !isStatic && !hideDistractions && (
                  <div
                    className={`absolute pointer-events-none select-none ${
                      overlay.position === "tl" ? "top-3 left-3"
                      : overlay.position === "tr" ? "top-3 right-3"
                      : overlay.position === "bl" ? "bottom-3 left-3"
                      : "bottom-3 right-3"
                    }`}
                    style={{ opacity: Math.max(0, Math.min(1, (overlay.opacity ?? 75) / 100)) }}
                    data-testid="live-overlay"
                  >
                    <div
                      className={
                        overlay.style === "boxed"
                          ? "px-2 py-0.5 text-xs md:text-sm tracking-[0.18em] font-bold text-[var(--phosphor)] bg-black/55 border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] backdrop-blur-[2px]"
                          : overlay.style === "shadow"
                          ? "text-base md:text-lg tracking-[0.18em] font-extrabold text-[var(--phosphor)]"
                          : "text-xs md:text-sm tracking-[0.18em] font-bold text-[var(--phosphor)] phosphor-glow"
                      }
                      style={overlay.style === "shadow"
                        ? { textShadow: "2px 2px 0 rgba(0,0,0,0.95), -1px -1px 0 rgba(0,0,0,0.7), 1px -1px 0 rgba(0,0,0,0.7), -1px 1px 0 rgba(0,0,0,0.7)" }
                        : undefined}
                      data-testid="live-overlay-text"
                    >
                      {overlay.text}
                    </div>
                  </div>
                )}


                </div>
                {/* YouTube bot-wall notice — surfaces only on genuine YT
                    embed failures (onError, or never-reached-PLAYING after
                    12s). The next-tape auto-skip is already queued; this
                    just tells the viewer what's about to happen. */}
                {ytBotGated && currentVideo?.kind === "youtube" && mode === "tv" && (
                  <div
                    className="absolute inset-0 z-30 flex items-center justify-center bg-black/80 pointer-events-none"
                    style={{ fontFamily: "VT323, monospace" }}
                    data-testid="yt-bot-gate"
                  >
                    <div className="text-[var(--phosphor)] phosphor-glow text-center px-6 max-w-md">
                      <div className="text-3xl md:text-4xl tracking-[0.2em] chromatic mb-3">
                        SIGNAL BLOCKED
                      </div>
                      <div className="text-lg md:text-xl opacity-90">
                        YouTube refused this embed. Tuning to the next tape…
                      </div>
                    </div>
                  </div>
                )}

                {/* "SOUND ON" confirmation flash — fires once when the
                    viewer's first gesture successfully un-mutes the YT
                    iframe. Auto-dismisses after 2.5s via animate-fade-in-out. */}
                {showSoundOnFlash && mode === "tv" && (
                  <div
                    className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 pointer-events-none animate-fade-in-out"
                    style={{ fontFamily: "VT323, monospace" }}
                    data-testid="sound-on-flash"
                  >
                    <div className="text-[var(--phosphor)] phosphor-glow chromatic text-base md:text-xl tracking-[0.3em] px-3 py-1 border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] bg-black/50">
                      ◢ SOUND ON ◣
                    </div>
                  </div>
                )}

                {/* Picture-mode OSD */}
                {showPictureOsd && (
                  <div
                    className="absolute top-4 right-4 z-[62] pointer-events-none border border-[var(--phosphor)] bg-black/90 px-3 py-2 text-[var(--phosphor)] phosphor-glow"
                    style={{ fontFamily: "VT323, monospace" }}
                    data-testid="osd-picture-mode"
                  >
                    PICTURE · {
                      pictureMode === "letterbox" ? "LETTERBOX"
                      : pictureMode === "14:9" ? "14:9"
                      : pictureMode === "pan-scan" ? "PAN & SCAN"
                      : pictureMode === "cinema-zoom" ? "CINEMA ZOOM"
                      : "STRETCH"
                    }
                  </div>
                )}

                {/* Scanline-intensity OSD — flashed for ~1.8s when the
                    viewer long-presses POWER to cycle the CRT scanline
                    look between OFF / NORMAL / HEAVY. */}
                {showScanlineOsd && (
                  <div
                    className="absolute top-6 left-1/2 -translate-x-1/2 z-30 pointer-events-none animate-fade-in-out"
                    style={{ fontFamily: "VT323, monospace" }}
                    data-testid="osd-scanlines"
                  >
                    <div className="text-[var(--phosphor)] phosphor-glow chromatic text-base md:text-xl tracking-[0.3em] px-3 py-1 border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] bg-black/55">
                      SCANLINES · {scanlineLabel || "NORMAL"}
                    </div>
                  </div>
                )}

                {/* WELCOME BACK is now rendered INSIDE the channel guide
                    as an inline banner (see `welcomeBack` prop on
                    `ChannelGuide.js`). The standalone CRT-overlay used
                    to live here at z-40, which left it occluded by the
                    z-40 guide on power-on. Integrating the two surfaces
                    eliminates the overlay collision and keeps the
                    welcome-back tied to the moment the viewer is
                    actually browsing. Local effect still fires the
                    `crt:welcome-back-show` window event with the full
                    payload — see the useEffect above. */}


                {showEpgPeek && mode === "tv" && Array.isArray(epgPeek) && epgPeek.length > 0 && (
                  <div
                    className="absolute inset-y-6 right-6 z-30 max-w-[55%] flex"
                    style={{ fontFamily: "VT323, monospace" }}
                    data-testid="epg-peek"
                    onClick={() => {
                      if (epgPeekTimer.current) clearTimeout(epgPeekTimer.current);
                      setShowEpgPeek(false);
                    }}
                  >
                    <div className="text-[var(--phosphor)] phosphor-glow border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] bg-black/70 backdrop-blur-sm w-full overflow-y-auto cursor-pointer">
                      <div className="text-base md:text-lg tracking-[0.35em] px-3 py-2 border-b border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] chromatic">
                        ┌── TV GUIDE · NOW ──┐
                      </div>
                      <ul className="px-3 py-2 space-y-1 text-sm md:text-base">
                        {epgPeek.map((row) => (
                          <li
                            key={row.id}
                            className={`flex gap-3 leading-tight ${row.isCurrent ? "text-[var(--phosphor)]" : "text-[color-mix(in_srgb,var(--phosphor)_70%,transparent)]"}`}
                          >
                            <span className="opacity-90">
                              {row.isCurrent ? "▶ " : "  "}CH {row.numberLabel}
                            </span>
                            <span className="truncate flex-1">
                              {row.name} <span className="opacity-60">— {row.nowTitle}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                      <div className="text-xs opacity-60 px-3 py-1 border-t border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] tracking-[0.2em]">
                        TAP TO DISMISS
                      </div>
                    </div>
                  </div>
                )}

                {/* Pledge-drive interstitial — SUPPORT THE STATION card
                    that briefly overlays the tube on some channel
                    changes when the operator has BMC enabled. Renders
                    inside the crt-screen so all the tube effects
                    (scanlines, curvature, vignette) apply to it too. */}
                {pledgeShowing && power && mode === "tv" && !isStatic && (
                  <PledgeDriveIdent
                    config={pledgeDrive}
                    onDismiss={() => setPledgeShowing(false)}
                  />
                )}

                {/* ★ THANK YOU ★ bumper — golden ident that fires ONCE
                    when a viewer returns from tipping. See the
                    visibilitychange effect above for the trigger
                    rules. */}
                {thanksShowing && power && mode === "tv" && !isStatic && (
                  <ThanksBumper onDismiss={() => setThanksShowing(false)} />
                )}

                {/* Live LOBBY chat — single global room shared by every
                    visitor regardless of which channel they're watching.
                    Stays open even during the SHOW STARTING SOON countdown
                    so viewers can chat while their video is loading. Only
                    hidden during the brief channel-change static flash. */}
                {liveChannelId && power && mode === "tv" && !isStatic && (
                  <LiveChat
                    messages={chatMessages}
                    pinned={chatPinned}
                    status={liveStatus}
                    nick={liveNick}
                    isOwnerNick={isOwnerNick}
                    channelId={liveChannelId}
                    viewerCount={viewerCount}
                    channelCounts={channelCounts}
                    tunedChannelId={tunedChannelId}
                    channelName={channelName}
                    channelNumberLabel={channelNumberLabel}
                    onSend={sendChat}
                    onRename={renameNick}
                    onPin={pinMessage}
                    onFirstSent={() => fireTrophy && fireTrophy("first_voice")}
                  />
                )}

                {/* (The previous standalone yt-mute-hint block was merged
                    into the unified viewer-tips card above.) */}
{/* Unified viewer-tips card. Replaces the previously-separate
                    "DBL-CLICK TO ENLARGE" + "CLICK ANYWHERE TO ENABLE SOUND"
                    overlays — both lines now share one centered card so they
                    can never overlap. Each line is conditional on its own
                    trigger, so the card shrinks to one line if only one tip
                    applies, and hides entirely when neither does. Copy is
                    owner-editable via SITE MESSAGES → VIEWER TIPS. */}
                {(() => {
                  const showSoundLine = !hasUserGesture && currentVideo?.kind === "youtube" && volume > 0;
                  // Fullscreen feature removed at owner's request — the
                  // "DBL-CLICK SCREEN TO ENLARGE" line no longer appears.
                  // Tip card only renders for the sound-on prompt now.
                  const anyTip = showSoundLine && currentVideo && !isStatic && mode === "tv";
                  if (!anyTip) return null;
                  const title = (viewerTips && viewerTips.title) || "VIEWER TIPS";
                  const soundText = (viewerTips && viewerTips.sound_line) || "CLICK ANYWHERE TO ENABLE SOUND";
                  return (
                    <div
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none"
                      style={{ fontFamily: "VT323, monospace" }}
                      data-testid="viewer-tips"
                    >
                      <div className="text-[var(--phosphor)] phosphor-glow border border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] bg-black/60 backdrop-blur-sm px-4 py-2 text-center shadow-[0_0_18px_color-mix(in srgb, var(--phosphor) 25%, transparent)]">
                        <div className="chromatic text-sm md:text-base tracking-[0.35em] opacity-85 mb-1">
                          ◢ {title} ◣
                        </div>
                        <div className="text-base md:text-xl tracking-[0.25em]" data-testid="yt-mute-hint">
                          {soundText}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Upload terminal layer */}
                {children}

                {/* Phase 2.3.61g.1 — GUI-safe glass edge.
                    No scanlines, vignette or source ageing over interface
                    content; retain only a restrained tube-edge presence. */}
                {isGuiProfile && (
                  <div
                    className="absolute inset-0 z-[85] pointer-events-none rounded-[14px]"
                    data-testid="crt-gui-glass-edge"
                    aria-hidden="true"
                    style={{
                      boxShadow:
                        "inset 0 0 0 1px color-mix(in srgb, var(--phosphor) 10%, transparent), inset 0 0 14px color-mix(in srgb, var(--phosphor) 5%, transparent)",
                    }}
                  />
                )}

                {/* OSD: Channel display — Trinitron-style LED chip top-right.
                    Compact (so it doesn't crowd the new bottom slate), with a
                    soft phosphor pulse on each channel change. */}
                {/* OSD: Digit buffer — shows the user's in-progress channel
                    number as they type ("CH 1_" → "CH 12"). Sits in the
                    same top-right slot as the regular channel OSD; the
                    channel OSD takes over the moment a tune actually
                    commits. Pre-empts the channel OSD when both would be
                    visible because the buffer is the more immediate piece
                    of feedback. Hidden when nothing's typed. */}
                {digitBuffer && mode === "tv" && (
                  <div
                    className="absolute top-5 right-6 z-50 pointer-events-none"
                    style={{ fontFamily: "VT323, monospace" }}
                    data-testid="osd-digit-buffer"
                  >
                    <div className="border-2 border-dashed border-[color-mix(in_srgb,var(--phosphor)_60%,transparent)] bg-black/75 backdrop-blur-sm shadow-[inset_0_0_8px_rgba(0,0,0,0.6),0_0_14px_color-mix(in srgb, var(--phosphor) 35%, transparent)] px-3 py-1 text-[var(--phosphor)] phosphor-glow">
                      <div className="text-3xl md:text-4xl leading-none tracking-[0.25em] tabular-nums chromatic text-right">
                        CH {digitBuffer.padStart(2, "0")}<span className="opacity-60 animate-pulse">_</span>
                      </div>
                      <div className="text-[10px] md:text-xs mt-0.5 opacity-60 tracking-widest text-right">
                        TUNE
                      </div>
                    </div>
                  </div>
                )}

                {/* OSD: Channel slate — replaces the digit-buffer OSD once
                    a tune commits. We hide it when the buffer is active
                    so the two slates don't stack on the same corner. */}
                {false && osdChannel && !digitBuffer && mode === "tv" && (
                  <div
                    className="absolute top-5 right-6 z-50 pointer-events-none"
                    style={{ fontFamily: "VT323, monospace" }}
                    data-testid="osd-channel"
                  >
                    <div className="border-2 border-[color-mix(in_srgb,var(--phosphor)_70%,transparent)] bg-black/75 backdrop-blur-sm shadow-[inset_0_0_8px_rgba(0,0,0,0.6),0_0_14px_color-mix(in srgb, var(--phosphor) 45%, transparent)] px-3 py-1 text-[var(--phosphor)] phosphor-glow animate-led-flash">
                      <div className="text-3xl md:text-4xl leading-none tracking-[0.25em] tabular-nums chromatic text-right">
                        CH {channelNumberLabel || String(channelNumber).padStart(2, "0")}
                      </div>
                      {channelName && (
                        <div className="text-xs md:text-sm mt-0.5 opacity-80 tracking-widest text-right truncate max-w-[14em]">
                          {channelName}
                        </div>
                      )}
                      {channelIsSubChannel && (
                        <div className="text-[9px] mt-0.5 tracking-[0.3em] text-right opacity-75" data-testid="sub-channel-badge-osd">
                          ↳ SUB · {(channelParentName || "").toUpperCase().slice(0, 22)}
                        </div>
                      )}
                      {videoCount > 0 && (
                        <div className="text-[10px] md:text-xs opacity-60 text-right tabular-nums">
                          {videoIndex + 1} / {videoCount}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* OSD: Volume bar */}
                {osdVolume && mode === "tv" && (
                  <div
                    className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 pointer-events-none flex flex-col items-center gap-2"
                    data-testid="osd-volume"
                  >
                    <div
                      className="text-[var(--phosphor)] phosphor-glow tracking-[0.3em] text-2xl"
                      style={{ fontFamily: "VT323, monospace" }}
                    >
                      VOL
                    </div>
                    <div className="flex gap-[6px]">
                      {Array.from({ length: 10 }).map((_, i) => (
                        <div
                          key={i}
                          className={`w-3 h-10 border ${
                            i < volume
                              ? "bg-[var(--phosphor)] border-[var(--phosphor)] shadow-[0_0_10px_var(--phosphor)]"
                              : "bg-transparent border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)]"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Glass reflection */}
          <div className="pointer-events-none absolute inset-0 z-40 rounded-[28px]"
               style={{
                 background:
                   "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,0) 65%, rgba(255,255,255,0.03) 100%)"
               }}
          />
          {isFullscreen && (
            <button
              type="button"
              className="sltv-mobile-fullscreen-exit sltv-mobile-fullscreen-exit-active"
              onClick={toggleMobileFullscreen}
              aria-label="Exit fullscreen"
              data-testid="mobile-fullscreen-exit"
              style={{
                position: "absolute",
                top: "max(0.65rem, env(safe-area-inset-top))",
                right: "max(0.65rem, env(safe-area-inset-right))",
                zIndex: 2147483647,
                display: "inline-flex",
                opacity: showMobileFsExit ? 1 : 0,
                transition: "opacity 220ms ease",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.38rem",
                minWidth: "5.25rem",
                minHeight: "2.8rem",
                padding: "0.45rem 0.7rem",
                border: "1px solid rgba(255,255,255,0.94)",
                borderRadius: "0.45rem",
                background: "rgba(0,0,0,0.86)",
                color: "#fff",
                fontFamily: "VT323, monospace",
                fontSize: "1rem",
                fontWeight: 700,
                letterSpacing: "0.14em",
                lineHeight: 1,
                textShadow: "0 1px 2px #000",
                boxShadow: "0 0 0 1px rgba(0,0,0,0.75), 0 4px 18px rgba(0,0,0,0.55)",
                WebkitTapHighlightColor: "transparent",
                touchAction: "manipulation",
                pointerEvents: showMobileFsExit ? "auto" : "none",
              }}
            >
              <span aria-hidden="true" style={{ fontFamily: "sans-serif", fontSize: "1.45rem", fontWeight: 300, lineHeight: 0.8 }}>×</span>
              EXIT
            </button>
          )}
        </div>

        {/* Cabinet brand strip — call-sign + viewer-count + power LED +
            (bottom-center) the ★ PATRON badge for tippers. The
            scrolling bezel ticker that used to live in the middle was
            removed; viewers get the same info from the GUIDE instead. */}
        <div
          className="cabinet-fascia mt-5 flex items-center justify-between gap-4 px-4 py-2.5 text-neutral-400"
          data-testid="cabinet-fascia"
        >
          {/* Left slot — SCARTLEAD.TV wordmark. Suppressed when the
              PATRON perk `hideDistractions` is on so tippers get a
              clean bezel. Renders an empty placeholder div so the flex
              layout still balances (center + right stay in position). */}
          {hideDistractions ? (
            <div className="shrink-0" data-testid="cabinet-brand-hidden" />
          ) : (
            <div
              className="cabinet-wordmark text-xs tracking-[0.4em] font-black shrink-0"
              style={{ fontFamily: "Chivo, sans-serif" }}
              data-testid="cabinet-brand-text"
            >
              {brandText || "SCARTLEAD.TV"}
            </div>
          )}

          {/* Center slot — ★ PATRON badge for viewers who've unlocked
              the trophy. Self-contained: renders null for non-patrons,
              so this cell is empty and invisible otherwise. */}
          <div className="flex-1 flex justify-center">
            <PatronBug />
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {/* Viewer-count chip — only shows when at least one viewer is
                connected (avoid "1 WATCHING" feeling lonely for solo users
                we render the count only when > 1). */}
            {viewerCount > 1 && (
              <div
                className="text-[10px] tracking-[0.25em] phosphor-glow text-[var(--phosphor)] border border-[color-mix(in_srgb,var(--phosphor)_50%,transparent)] px-2 py-[1px] bg-black/40"
                style={{ fontFamily: "VT323, monospace" }}
                data-testid="viewer-count"
              >
                ● {viewerCount} WATCHING
              </div>
            )}
            <div
              className="cabinet-model text-[10px] tracking-[0.3em]"
              style={{ fontFamily: "Chivo, sans-serif" }}
              data-testid="cabinet-callsign"
            >
              {callsign || "CRT-1986"}
            </div>
            <div
              className={`cabinet-power-lens w-2.5 h-2.5 rounded-full ${power ? "cabinet-power-lens-on bg-[var(--phosphor)]" : shuttingDown ? "cabinet-power-lens-shutdown" : "cabinet-power-lens-off bg-red-900"}`}
              data-testid="cabinet-led"
            />
          </div>
        </div>
      </div>

      {/* TV stand/legs */}
      <div className="cabinet-feet mx-auto mt-1 flex justify-center" data-testid="cabinet-feet">
        <div className="cabinet-foot" data-testid="cabinet-foot-left" />
        <div className="cabinet-foot" data-testid="cabinet-foot-right" />
      </div>
    </div>
  );
}
